import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getPublicPlanById, type PublicPlan } from '@data/publicPlans';
import { addOffer, type Offer } from '@data/offers';
import { createOffer, getOffersForPlan, updateOffer } from '@services/offers';
import { createIssue } from '@services/issues';
import { getCompanyGardeners, checkMultipleGardenersAvailability, getBulkTimeOff, type GardenerResponse, type AvailabilityResult } from '@services/gardeners';
import { getCompanySchedule, getMyCompany, getMyOffersPermission } from '@services/companies';
import { stylePresetsByKey } from '@data/stylePresets';
import { notifyOfferReceived } from '@data/notifications';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getAdminEffectiveCompanyOrGardenerIdNum } from '../../lib/adminViewAs';
import {
	generateTimelineSummary,
	generateSyntheticGanttData,
	ganttTasksFromPlanInstructions,
	ganttTasksFromStructuredTimeline,
	defaultOfferTimelineEstimate,
	isDefaultOfferTimelineEstimate,
	timelineSummaryTextFromPlanInstructions,
	applyWorkdayTaskUpdate,
	adjustTaskDatesForPTO,
	scheduleTimelineTasksSequentially,
	moveTimelineTaskInOrder,
	syncAiContentFromTimeline,
	stripLeadingInstructionDayLabel,
	autoAssignGardenersToTimelineTasks,
	resolveTaskRequiredGardeners,
	taskHasAssignedGardeners,
	getTaskAssigneeIds,
	endDateForWorkingDays,
	normalizeCompanyCalendar,
	type CompanyCalendar
} from '@utils/ganttDataGenerator';
import type { GanttTask } from '../../types/gantt';
import { useAlertDialog } from '../../shared/AlertDialog';
import BackButton from '../../shared/BackButton';
import PlanGenerationPuzzleAnimation from '../../shared/PlanGenerationPuzzleAnimation';
import GanttChart from '../../components/GanttChart';
import { generatePlanPDF } from '@utils/pdfGenerator';
import { getImageUrl } from '@utils/imageUrl';
import ImageModal from '../../shared/ImageModal';
import { generateAIPlan } from '@services/aiPlanGenerator';
import type { AIGeneratedContent } from '@data/publicPlans';
import { getComponentLabel } from '@data/componentLabels';
import { useI18n } from '../../i18n';
import {
	getMockMaterialAvailability,
	getSkillsCovered,
	getMissingSkills,
	MOCK_SKILLS_NEEDED_FOR_PROJECT,
	SKILLS_OPTIONS
} from '@data/mockOfferFeatures';
import { useTranslatedAIContent } from '../../shared/useTranslatedAIContent';
import { getSkillLabel } from '@utils/skillLabels';
import { addCompanyWorkdays, nextCompanyWorkingDay } from '@utils/companySchedule';
import { getTaskLegalMeta } from '@utils/taskLegalStatus';
import ShareClientLinkModal from '@components/plan/ShareClientLinkModal';
import TaskGardenerPicker from '@components/plan/TaskGardenerPicker';
import { getCompanyInviteForPlan, type CompanyPlanInviteStatus } from '@services/planInvites';
import { normalizeShareVisibility } from '@utils/shareVisibility';
import { getMaterialsPlusLaborTotal, normalizePricingBreakdown } from '@utils/pricingBreakdownUtils';

// Helper function to format instruction text with highlights
function formatInstruction(instruction: string): JSX.Element {
	// Split by colon to separate title from description
	const colonIndex = instruction.indexOf(':');
	if (colonIndex === -1) {
		// No colon, return as-is with basic formatting
		return <span>{formatText(instruction)}</span>;
	}
	
	const title = instruction.substring(0, colonIndex);
	const description = stripLeadingInstructionDayLabel(instruction.substring(colonIndex + 1));
	
	return (
		<>
			<span className="fw-bold" style={{ color: '#198754' }}>{formatText(title)}:</span>
			<span> {formatText(description)}</span>
		</>
	);
}

// Helper to format text with highlights for measurements, numbers, and key terms
function formatText(text: string): JSX.Element[] {
	const parts: JSX.Element[] = [];
	let lastIndex = 0;
	
	// Patterns to highlight:
	// 1. Timeframes: "Days 1-2", "Day 23", "24-48 hours"
	// 2. Measurements: "150-200mm", "300mm", "2-3 bags", "10-10-10"
	// 3. Percentages: "2%", "50%"
	// 4. Material names in parentheses
	// 5. Important action verbs
	
	const patterns = [
		// Timeframes
		/(Days? \d+-\d+|Day \d+|\d+-\d+ hours?)/gi,
		// Measurements with units
		/(\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*(?:mm|cm|m|kg|bags?|pcs?|m²|m³|lm|times?|weeks?|months?))/gi,
		// Percentages
		/(\d+(?:\.\d+)?%)/gi,
		// Numbers followed by "x" (dimensions like "1m x 1m")
		/(\d+(?:\.\d+)?\s*x\s*\d+(?:\.\d+)?)/gi,
		// NPK ratios
		/(\d+-\d+-\d+)/g,
		// Material names in parentheses (e.g., "(bark chips, compost)")
		/(\([^)]+\))/g,
	];
	
	// Find all matches
	const matches: Array<{ start: number; end: number; text: string }> = [];
	
	patterns.forEach(pattern => {
		let match;
		while ((match = pattern.exec(text)) !== null) {
			matches.push({
				start: match.index,
				end: match.index + match[0].length,
				text: match[0]
			});
		}
	});
	
	// Sort matches by position
	matches.sort((a, b) => a.start - b.start);
	
	// Remove overlapping matches (keep first)
	const nonOverlapping: Array<{ start: number; end: number; text: string }> = [];
	for (const match of matches) {
		if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
			nonOverlapping.push(match);
		}
	}
	
	// Build parts
	nonOverlapping.forEach((match, idx) => {
		// Add text before match
		if (match.start > lastIndex) {
			parts.push(<span key={`text-${idx}`}>{text.substring(lastIndex, match.start)}</span>);
		}
		// Determine highlight style based on content
		const isParentheses = match.text.startsWith('(');
		const isTimeframe = /Days?|hours?/i.test(match.text);
		const highlightClass = isParentheses 
			? "fw-semibold" 
			: isTimeframe 
				? "fw-bold"
				: "fw-bold";
		
		// Use green tones with different shades
		const highlightStyle: React.CSSProperties = isParentheses 
			? { color: '#198754', opacity: 0.8 } // Lighter green for parentheses
			: isTimeframe 
				? { color: '#157347' } // Darker green for timeframes
				: { color: '#198754' }; // Standard green for measurements
		
		// Add highlighted match
		parts.push(
			<span key={`highlight-${idx}`} className={highlightClass} style={highlightStyle}>
				{match.text}
			</span>
		);
		lastIndex = match.end;
	});
	
	// Add remaining text
	if (lastIndex < text.length) {
		parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
	}
	
	// If no matches, return original text
	if (parts.length === 0) {
		return [<span key="full">{text}</span>];
	}
	
	return parts;
}

function getLocalizedStyleLabel(
	styleKey: string | undefined,
	fallbackTitle: string | undefined,
	copy: (en: string, de: string, hr: string) => string
): string {
	switch (styleKey) {
		case 'modern': return copy('Modern', 'Modern', 'Moderno');
		case 'mediterranean': return copy('Mediterranean', 'Mediterran', 'Mediteranski');
		case 'minimalist': return copy('Minimalist', 'Minimalistisch', 'Minimalistički');
		case 'cottage': return copy('Cottage', 'Landhausstil', 'Seoski');
		case 'japanese': return copy('Japanese Zen', 'Japanischer Zen', 'Japanski zen');
		case 'tropical': return copy('Tropical', 'Tropisch', 'Tropski');
		case 'rustic': return copy('Rustic', 'Rustikal', 'Rustikalni');
		case 'coastal': return copy('Coastal', 'Küstenstil', 'Obalni');
		case 'scandinavian': return copy('Scandinavian', 'Skandinavisch', 'Skandinavski');
		case 'traditional': return copy('Traditional', 'Traditionell', 'Tradicionalni');
		default: return fallbackTitle ?? styleKey ?? '';
	}
}

function formatLocalizedLeadTime(
	leadTime: string | undefined,
	copy: (en: string, de: string, hr: string) => string
): string {
	if (!leadTime) return '';
	return leadTime.replace(/\bdays?\b/gi, copy('days', 'Tage', 'dana'));
}

// Helper to format key features with highlights
function formatFeatureText(feature: string): JSX.Element[] {
	const parts: JSX.Element[] = [];
	let lastIndex = 0;
	
	// Extract the main feature description (usually first part)
	const commaIndex = feature.indexOf(',');
	const firstPart = commaIndex > 0 ? feature.substring(0, commaIndex) : feature.substring(0, Math.min(60, feature.length));
	
	// Find capitalized terms and numbers
	const matches: Array<{ start: number; end: number; text: string; isMain: boolean }> = [];
	
	// Highlight the first part (main feature description)
	if (firstPart.length > 0) {
		matches.push({
			start: 0,
			end: firstPart.length,
			text: firstPart,
			isMain: true
		});
	}
	
	// Find numbers
	let match;
	const numberPattern = /(\d+(?:\.\d+)?\s*(?:mm|cm|m|kg|%|m²|m³))/gi;
	while ((match = numberPattern.exec(feature)) !== null) {
		matches.push({
			start: match.index,
			end: match.index + match[0].length,
			text: match[0],
			isMain: false
		});
	}
	
	// Sort and deduplicate
	matches.sort((a, b) => a.start - b.start);
	const nonOverlapping: Array<{ start: number; end: number; text: string; isMain: boolean }> = [];
	for (const match of matches) {
		if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
			nonOverlapping.push(match);
		}
	}
	
	// Build parts
	nonOverlapping.forEach((match, idx) => {
		if (match.start > lastIndex) {
			parts.push(<span key={`text-${idx}`}>{feature.substring(lastIndex, match.start)}</span>);
		}
		if (match.isMain) {
			parts.push(
				<span key={`main-${idx}`} className="fw-bold" style={{ color: '#198754' }}>
					{match.text}
				</span>
			);
		} else {
			parts.push(
				<span key={`highlight-${idx}`} className="fw-bold" style={{ color: '#198754' }}>
					{match.text}
				</span>
			);
		}
		lastIndex = match.end;
	});
	
	if (lastIndex < feature.length) {
		parts.push(<span key="text-end">{feature.substring(lastIndex)}</span>);
	}
	
	if (parts.length === 0) {
		return [<span key="full">{feature}</span>];
	}
	
	return parts;
}

export default function PlanOffer() {
	const { planId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const offerReturnTo =
		typeof (location.state as { returnTo?: string } | null)?.returnTo === 'string'
			? (location.state as { returnTo: string }).returnTo
			: undefined;
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const companyActorId = useMemo(
		() => getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget),
		[user, viewAsTarget]
	);
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const localizeProjectFocus = (focus?: string) => {
		switch (focus) {
			case 'planting_only':
				return copy('Planting only', 'Nur Bepflanzung', 'Samo sadnja');
			case 'construction_hardscape_only':
				return copy('Construction / hardscape only', 'Nur Bau / Hardscape', 'Samo gradnja / hardscape');
			case 'mixed_planting_construction':
				return copy('Mixed (planting + construction)', 'Gemischt (Bepflanzung + Bau)', 'Mješovito (sadnja + gradnja)');
			default:
				return focus || copy('Not specified', 'Nicht angegeben', 'Nije navedeno');
		}
	};
	const [offerFlowAllowed, setOfferFlowAllowed] = useState<boolean | null>(null);
	const { showAlert } = useAlertDialog();
	const [plan, setPlan] = useState<PublicPlan | undefined>(() => (planId ? getPublicPlanById(planId) : undefined));
	// Business/company name: use logged-in user's company name (no manual field needed)
	const [gardenerName, setGardenerName] = useState('');
	const [estimatedPrice, setEstimatedPrice] = useState(1200);
	const [notes, setNotes] = useState('');
	const [isSending, setIsSending] = useState(false);
	const isSendingRef = useRef(false);
	const [existingOfferId, setExistingOfferId] = useState<number | null>(null);
	const [isReportingUser, setIsReportingUser] = useState(false);
	const defaultTimelineFallback = defaultOfferTimelineEstimate(language);
	const [timelineEstimate, setTimelineEstimate] = useState(() => defaultOfferTimelineEstimate(language));
	const [projectStartDate, setProjectStartDate] = useState(() => {
		const date = new Date();
		date.setDate(date.getDate() + 14); // Default: 2 weeks from now
		return date.toISOString().split('T')[0];
	});
	const [hasTouchedProjectStartDate, setHasTouchedProjectStartDate] = useState(false);
	const [validUntil, setValidUntil] = useState(() => {
		const date = new Date();
		date.setDate(date.getDate() + 30); // Default: 30 days from now
		return date.toISOString().split('T')[0];
	});
	const [hasTouchedValidUntil, setHasTouchedValidUntil] = useState(false);
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [imageModalIndex, setImageModalIndex] = useState(0);

	// Offer-only plan state: edits/regenerate apply only to this offer; marketplace plan is never updated
	const [offerPlanContent, setOfferPlanContent] = useState<AIGeneratedContent | null>(null);
	// Plan revision state (change something? + version browsing in offer wizard)
	const [viewingVersionKey, setViewingVersionKey] = useState<string>('current');
	const [offerRevisions, setOfferRevisions] = useState<AIGeneratedContent[]>([]);
	const [isRegenerating, setIsRegenerating] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editInstructions, setEditInstructions] = useState('');
	const [isRestoring, setIsRestoring] = useState(false);
	
	// Gardener selection state
	const [availableGardeners, setAvailableGardeners] = useState<GardenerResponse[]>([]);
	const [selectedGardeners, setSelectedGardeners] = useState<number[]>([]);
	const [gardenerSkillsOverrides, setGardenerSkillsOverrides] = useState<Record<number, string[]>>({});
	const [editingSkillsForGardenerId, setEditingSkillsForGardenerId] = useState<number | null>(null);
	const [isLoadingGardeners, setIsLoadingGardeners] = useState(false);
	const [availabilityResults, setAvailabilityResults] = useState<AvailabilityResult[]>([]);
	const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
	const [gardenerSearch, setGardenerSearch] = useState('');
	const [skillFilter, setSkillFilter] = useState<string>('all');
	const [availabilityFilter, setAvailabilityFilter] = useState<'all' | 'available' | 'has_conflict'>('all');
	const [showAllGardeners, setShowAllGardeners] = useState(false);
	
	// Timeline editing state
	const [timelineTasks, setTimelineTasks] = useState<GanttTask[]>([]);
	const [isAutoAssigningTimeline, setIsAutoAssigningTimeline] = useState(false);
	const [autoAssignChoiceOpen, setAutoAssignChoiceOpen] = useState(false);
	const [showTimelineDateEditHint, setShowTimelineDateEditHint] = useState(false);
	// Company schedule and PTO for smart timeline (working days + PTO-aware dates)
	const [companySchedule, setCompanySchedule] = useState<{ working_days: number[] | null; non_working_dates: { date: string; label?: string | null }[] } | null>(null);
	const [companyCountryCode, setCompanyCountryCode] = useState<string | null>(null);
	const [ptoByGardenerId, setPtoByGardenerId] = useState<Record<string, Array<{ start_date: string; end_date: string; label?: string | null }>>>({});
	const [isReschedulingLegalTasks, setIsReschedulingLegalTasks] = useState(false);
	const [timelineManuallyEdited, setTimelineManuallyEdited] = useState(false);
	const [timelineReorderNotice, setTimelineReorderNotice] = useState(false);

	// Plan content (description, features, instructions, pricing, tools): display by default, one Edit for entire section
	const [isEditingPlanContent, setIsEditingPlanContent] = useState(false);
	const hasOfferSpecificEdits = offerPlanContent !== null || offerRevisions.length > 0;

	const [companyInviteStatus, setCompanyInviteStatus] = useState<CompanyPlanInviteStatus | null>(null);
	const [shareClientModalOpen, setShareClientModalOpen] = useState(false);
	const [shareOfferId, setShareOfferId] = useState<number | null>(null);

	const isCompanyDirectFlow = user?.role === 'company' || user?.role === 'admin';
	const linkedClientUserId = companyInviteStatus?.used_by_user_id ?? null;
	const planOwnerUserIdNum = plan?.seekerUserId ? parseInt(String(plan.seekerUserId), 10) : 0;
	const hasMarketplaceSeeker =
		Number.isInteger(planOwnerUserIdNum) &&
		planOwnerUserIdNum > 0 &&
		companyActorId != null &&
		planOwnerUserIdNum !== companyActorId;
	const clientIsLinked =
		Boolean(plan?.hasLinkedSeeker) ||
		(linkedClientUserId != null && linkedClientUserId > 0) ||
		hasMarketplaceSeeker;
	const companyShareVisibility = normalizeShareVisibility(companyInviteStatus?.share_visibility);

	// Normalize arbitrary AI skill labels to the fixed skill keys used in Settings / offers.
	const normalizeSkillsToOptions = useCallback((rawSkills: unknown): string[] => {
		if (!Array.isArray(rawSkills)) return [];
		const out: string[] = [];
		rawSkills.forEach((s) => {
			if (typeof s !== 'string') return;
			const trimmed = s.trim();
			if (!trimmed) return;
			// Exact match first (expected case when AI follows the prompt)
			if (SKILLS_OPTIONS.includes(trimmed)) {
				out.push(trimmed);
				return;
			}
			// Fallback: simple contains match on known skill keys (handles slightly descriptive phrases)
			const lower = trimmed.toLowerCase();
			const fuzzy = SKILLS_OPTIONS.find((key) => {
				const keyWord = key.replace(/_/g, ' ');
				return lower.includes(keyWord);
			});
			if (fuzzy) {
				out.push(fuzzy);
			}
		});
		return Array.from(new Set(out));
	}, []);

	const projectStartDateInputRef = useRef<HTMLInputElement | null>(null);
	const projectStartDateTimelineRef = useRef<HTMLInputElement | null>(null);
	const validUntilInputRef = useRef<HTMLInputElement | null>(null);

	const gardenerSkillsMap = useMemo(() => {
		return (id: number): string[] => {
			if (gardenerSkillsOverrides[id] !== undefined) return gardenerSkillsOverrides[id];
			const g = availableGardeners.find(a => a.id === id);
			return g?.skills ?? [];
		};
	}, [availableGardeners, gardenerSkillsOverrides]);

	const filteredGardeners = useMemo(() => {
		const search = gardenerSearch.trim().toLowerCase();
		const hasAvailabilityData = availabilityResults.length > 0;

		let list = [...availableGardeners];

		if (search) {
			list = list.filter(g => {
				const text = `${g.display_name || ''} ${g.name || ''} ${g.email || ''}`.toLowerCase();
				return text.includes(search);
			});
		}

		if (skillFilter !== 'all') {
			list = list.filter(g => gardenerSkillsMap(g.id).includes(skillFilter));
		}

		if (availabilityFilter !== 'all') {
			list = list.filter(g => {
				if (!hasAvailabilityData) return true;
				const availability = availabilityResults.find(
					r => Number(r.gardener_id) === Number(g.id)
				);
				// If we don't have data for this gardener, treat them as "unknown" and
				// exclude them from both filtered views so the counts are precise.
				if (!availability) return false;
				if (availabilityFilter === 'available') return availability.available;
				if (availabilityFilter === 'has_conflict') return !availability.available;
				return true;
			});
		}

		// Keep selected gardeners at the top, then sort alphabetically
		list.sort((a, b) => {
			const aSelected = selectedGardeners.includes(a.id);
			const bSelected = selectedGardeners.includes(b.id);
			if (aSelected !== bSelected) return aSelected ? -1 : 1;
			const aName = (a.display_name || a.name || a.email || '').toLowerCase();
			const bName = (b.display_name || b.name || b.email || '').toLowerCase();
			return aName.localeCompare(bName);
		});

		return list;
	}, [availableGardeners, gardenerSearch, skillFilter, availabilityFilter, availabilityResults, gardenerSkillsMap, selectedGardeners]);

	const seekerBudget = useMemo(() => {
		const range = plan?.questionnaire?.budgetRange;
		if (!range) return copy('Not specified', 'Nicht angegeben', 'Nije navedeno');
		return `${range.min.toLocaleString()} € – ${range.max.toLocaleString()} €`;
	}, [plan, language]);

	const seekerTimeline = useMemo(() => {
		const tl = plan?.questionnaire?.timeline;
		if (!tl || !tl.trim()) return copy('Not specified', 'Nicht angegeben', 'Nije navedeno');
		return tl;
	}, [plan, language]);

	const preferredStartDate = useMemo(() => {
		return plan?.questionnaire?.projectStartDate ?? null;
	}, [plan]);

	const isProjectStartBeforePreferred = useMemo(() => {
		if (!preferredStartDate || !projectStartDate) return false;
		return new Date(projectStartDate) < new Date(preferredStartDate);
	}, [preferredStartDate, projectStartDate]);

	const isValidUntilTooEarly = useMemo(() => {
		if (!preferredStartDate || !validUntil) return false;
		const preferred = new Date(preferredStartDate);
		const valid = new Date(validUntil);
		const diffDays = Math.floor((preferred.getTime() - valid.getTime()) / (1000 * 60 * 60 * 24));
		return diffDays > 7;
	}, [preferredStartDate, validUntil]);

	// Displayed AI content: selected version in offer wizard or current offer content
	const baseDisplayedAiContent = useMemo(() => {
		if (!plan?.aiGenerated) return undefined;
		if (viewingVersionKey === 'current') {
			return offerPlanContent ?? plan.aiGenerated;
		}
		if (viewingVersionKey.startsWith('plan-')) {
			const planRevisionIndex = Number(viewingVersionKey.replace('plan-', ''));
			if (!Number.isNaN(planRevisionIndex) && plan.revisions?.[planRevisionIndex]) {
				return { ...plan.aiGenerated, ...plan.revisions[planRevisionIndex] };
			}
		}
		if (viewingVersionKey.startsWith('offer-')) {
			const offerRevisionIndex = Number(viewingVersionKey.replace('offer-', ''));
			if (!Number.isNaN(offerRevisionIndex) && offerRevisions[offerRevisionIndex]) {
				return offerRevisions[offerRevisionIndex];
			}
		}
		return offerPlanContent ?? plan.aiGenerated;
	}, [plan, viewingVersionKey, offerPlanContent, offerRevisions]);
	const { translatedContent: translatedDisplayedAiContent } = useTranslatedAIContent(baseDisplayedAiContent ?? null);
	const displayedAiContent = translatedDisplayedAiContent ?? baseDisplayedAiContent;

	// Skills needed for this plan: from AI-generated plan (normalized to known skills), otherwise fallback mock list
	const skillsNeededForPlan = useMemo(() => {
		const fromPlan = normalizeSkillsToOptions(displayedAiContent?.required_skills);
		if (fromPlan.length > 0) return fromPlan;
		return MOCK_SKILLS_NEEDED_FOR_PROJECT;
	}, [displayedAiContent?.required_skills, normalizeSkillsToOptions]);

	const planRecommendedTeamSize = useMemo(() => {
		const n = Number(displayedAiContent?.recommended_team_size);
		if (Number.isFinite(n) && n >= 1) return Math.min(10, Math.floor(n));
		return 2;
	}, [displayedAiContent?.recommended_team_size]);

	// Load available gardeners for the company
	useEffect(() => {
		if (companyActorId == null) return;

		const loadGardeners = async () => {
			setIsLoadingGardeners(true);
			try {
				const gardeners = await getCompanyGardeners(companyActorId);
				setAvailableGardeners(gardeners);
			} catch (err) {
				console.error('Failed to load gardeners:', err);
			} finally {
				setIsLoadingGardeners(false);
			}
		};

		loadGardeners();
	}, [companyActorId]);

	// Load company working days and non-working dates (for smart timeline)
	useEffect(() => {
		if (companyActorId == null) return;
		let cancelled = false;
		getCompanySchedule(String(companyActorId))
			.then((schedule) => {
				if (!cancelled) {
					setCompanySchedule({
						working_days: schedule.working_days ?? null,
						non_working_dates: Array.isArray(schedule.non_working_dates) ? schedule.non_working_dates : []
					});
				}
			})
			.catch(() => {
				if (!cancelled) setCompanySchedule(null);
			});
		return () => { cancelled = true; };
	}, [companyActorId]);

	useEffect(() => {
		if (companyActorId == null) return;
		let cancelled = false;
		getMyCompany(String(companyActorId))
			.then((res) => {
				if (cancelled) return;
				const code = String(res?.company?.country_code ?? '').trim().toUpperCase();
				setCompanyCountryCode(code || null);
			})
			.catch(() => {
				if (!cancelled) setCompanyCountryCode(null);
			});
		return () => { cancelled = true; };
	}, [companyActorId]);

	// Load PTO for selected gardeners (for PTO-aware timeline)
	useEffect(() => {
		if (companyActorId == null || selectedGardeners.length === 0) {
			setPtoByGardenerId({});
			return;
		}
		let cancelled = false;
		getBulkTimeOff(companyActorId, selectedGardeners)
			.then((data) => {
				if (!cancelled) setPtoByGardenerId(data);
			})
			.catch(() => {
				if (!cancelled) setPtoByGardenerId({});
			});
		return () => { cancelled = true; };
	}, [companyActorId, selectedGardeners]);

	// Sync offer "sender" name from logged-in user (company name or fallback to user name)
	useEffect(() => {
		if (user?.role === 'admin' && viewAsTarget && (viewAsTarget.role === 'company' || viewAsTarget.role === 'gardener')) {
			const n = viewAsTarget.name?.trim();
			if (n) setGardenerName(n);
			return;
		}
		if (user?.companyName) setGardenerName(user.companyName);
		else if (user?.name) setGardenerName(user.name);
		else setGardenerName('');
	}, [user?.companyName, user?.name, user?.role, viewAsTarget]);

	// Build company calendar for gantt helpers (Mon–Fri default until company schedule loads)
	const companyCalendar = useMemo((): CompanyCalendar => {
		return normalizeCompanyCalendar(
			companySchedule
				? {
						workingDays: companySchedule.working_days ?? null,
						nonWorkingDates: companySchedule.non_working_dates ?? []
					}
				: null
		);
	}, [companySchedule]);

	const seasonalBanConflicts = useMemo(() => {
		return timelineTasks.filter((task) => getTaskLegalMeta(task, companyCountryCode)?.status === 'date_conflict');
	}, [timelineTasks, companyCountryCode]);

	const availabilityResultsByGardenerId = useMemo(() => {
		const map = new Map<string, AvailabilityResult>();
		availabilityResults.forEach((result) => {
			map.set(String(result.gardener_id), result);
		});
		return map;
	}, [availabilityResults]);

	const shiftDateByWorkdays = useCallback(
		(from: Date, workdays: number) => {
			if (companyCalendar) {
				return addCompanyWorkdays(from, workdays, companyCalendar.workingDays, companyCalendar.nonWorkingDates);
			}
			const d = new Date(from);
			let remaining = workdays;
			while (remaining > 0) {
				d.setDate(d.getDate() + 1);
				const weekday = d.getDay();
				if (weekday !== 0 && weekday !== 6) remaining--;
			}
			return d;
		},
		[companyCalendar]
	);

	const nextLegalWorkingDate = useCallback(
		(from: Date) => {
			if (companyCalendar) {
				return nextCompanyWorkingDay(from, companyCalendar.workingDays, companyCalendar.nonWorkingDates);
			}
			const d = new Date(from);
			while (d.getDay() === 0 || d.getDay() === 6) {
				d.setDate(d.getDate() + 1);
			}
			return d;
		},
		[companyCalendar]
	);

	const hasGardenerDateConflict = useCallback(
		(startDate: Date, endDate: Date, gardenerIds: string[]) => {
			const startOnly = startDate.toISOString().split('T')[0];
			const endOnly = endDate.toISOString().split('T')[0];
			for (const gardenerId of gardenerIds) {
				const ptoEntries = ptoByGardenerId[gardenerId] || [];
				const hasPtoConflict = ptoEntries.some((entry) => !(entry.end_date < startOnly || entry.start_date > endOnly));
				if (hasPtoConflict) return true;

				const availability = availabilityResultsByGardenerId.get(String(gardenerId));
				const projectConflicts = availability?.conflicts || [];
				const hasProjectConflict = projectConflicts.some(
					(conflict) => !(conflict.conflict_end < startOnly || conflict.conflict_start > endOnly)
				);
				if (hasProjectConflict) return true;
			}
			return false;
		},
		[availabilityResultsByGardenerId, ptoByGardenerId]
	);

	const getForwardWorkdayShift = useCallback(
		(from: Date, to: Date) => {
			if (to <= from) return 0;
			const cursor = new Date(from);
			cursor.setHours(12, 0, 0, 0);
			const target = new Date(to);
			target.setHours(12, 0, 0, 0);
			let shift = 0;
			while (cursor < target) {
				cursor.setDate(cursor.getDate() + 1);
				const normalized = nextLegalWorkingDate(new Date(cursor));
				if (
					normalized.getFullYear() === cursor.getFullYear() &&
					normalized.getMonth() === cursor.getMonth() &&
					normalized.getDate() === cursor.getDate()
				) {
					shift++;
				}
			}
			return shift;
		},
		[nextLegalWorkingDate]
	);

	const handleRescheduleSeasonalBanTasks = useCallback(() => {
		if (!projectStartDate || companyCountryCode !== 'DE' || seasonalBanConflicts.length === 0) return;
		setIsReschedulingLegalTasks(true);
		setTimelineTasks((prev) => {
			let next = [...prev];
			const tasksToMove = [...seasonalBanConflicts].sort(
				(a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
			);

			for (const conflictedTask of tasksToMove) {
				const current = next.find((task) => task.id === conflictedTask.id);
				if (!current) continue;

				const currentStart = new Date(current.startDate);
				const currentEnd = new Date(current.endDate);
				const restrictedYear = currentStart.getMonth() >= 2 ? currentStart.getFullYear() : currentEnd.getFullYear();
				const firstLegalDate = nextLegalWorkingDate(new Date(`${restrictedYear}-10-01T12:00:00`));
				const duration = typeof current.duration === 'number' && current.duration > 0 ? current.duration : 1;
				const assignedGardeners =
					Array.isArray(current.gardenerIds) && current.gardenerIds.length > 0
						? current.gardenerIds.map(String)
						: current.gardenerId
							? [String(current.gardenerId)]
							: [];

				let candidateStart = new Date(firstLegalDate);
				let candidateEnd = endDateForWorkingDays(candidateStart, duration, companyCalendar ?? undefined);
				let guard = 0;
				while (assignedGardeners.length > 0 && hasGardenerDateConflict(candidateStart, candidateEnd, assignedGardeners) && guard < 370) {
					const nextDay = new Date(candidateStart);
					nextDay.setDate(nextDay.getDate() + 1);
					candidateStart = nextLegalWorkingDate(nextDay);
					candidateEnd = endDateForWorkingDays(candidateStart, duration, companyCalendar ?? undefined);
					guard++;
				}

				const currentStartMs = new Date(current.startDate).getTime();
				const sortedByStart = [...next].sort(
					(a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
				);
				const tailTaskIds = new Set(
					sortedByStart
						.filter((task) => new Date(task.startDate).getTime() >= currentStartMs)
						.map((task) => task.id)
				);
				const shiftByWorkdays = getForwardWorkdayShift(new Date(current.startDate), candidateStart);

				next = next.map((task) => {
					if (!tailTaskIds.has(task.id)) return task;
					if (task.id === current.id) {
						return {
							...task,
							startDate: candidateStart.toISOString(),
							endDate: candidateEnd.toISOString(),
							duration,
						};
					}
					const shiftedStart = shiftDateByWorkdays(new Date(task.startDate), shiftByWorkdays);
					const taskDuration = typeof task.duration === 'number' && task.duration > 0 ? task.duration : 1;
					const shiftedEnd = endDateForWorkingDays(shiftedStart, taskDuration, companyCalendar ?? undefined);
					return {
						...task,
						startDate: shiftedStart.toISOString(),
						endDate: shiftedEnd.toISOString(),
						duration: taskDuration,
					};
				});

				// Keep timeline order stable after legal rescheduling:
				// once the restricted task is moved, everything after it must remain after it.
				const chronologicalTail = next
					.filter((task) => tailTaskIds.has(task.id))
					.sort((a, b) => {
						if (a.id === current.id) return -1;
						if (b.id === current.id) return 1;
						return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
					});
				let cursor = new Date(candidateEnd);
				for (let i = 1; i < chronologicalTail.length; i++) {
					const tailTask = chronologicalTail[i];
					const durationTail =
						typeof tailTask.duration === 'number' && tailTask.duration > 0 ? tailTask.duration : 1;
					const nextStart = nextLegalWorkingDate(new Date(cursor.getTime() + 24 * 60 * 60 * 1000));
					const nextEnd = endDateForWorkingDays(nextStart, durationTail, companyCalendar ?? undefined);
					next = next.map((task) =>
						task.id === tailTask.id
							? {
								...task,
								startDate: nextStart.toISOString(),
								endDate: nextEnd.toISOString(),
								duration: durationTail,
							}
							: task
					);
					cursor = new Date(nextEnd);
				}
			}

			return adjustTaskDatesForPTO(next, new Date(projectStartDate), companyCalendar, ptoByGardenerId);
		});
		setIsReschedulingLegalTasks(false);
	}, [
		projectStartDate,
		companyCountryCode,
		seasonalBanConflicts,
		nextLegalWorkingDate,
		shiftDateByWorkdays,
		getForwardWorkdayShift,
		hasGardenerDateConflict,
		companyCalendar,
		ptoByGardenerId,
	]);

	const commitTimelineTasks = useCallback(
		(nextTasks: GanttTask[], options?: { syncPlan?: boolean; markManual?: boolean; clearReorderNotice?: boolean }) => {
			if (!projectStartDate) {
				setTimelineTasks(nextTasks);
				return;
			}
			let tasks = nextTasks;
			if (selectedGardeners.length > 0 && availableGardeners.length > 0) {
				tasks = adjustTaskDatesForPTO(tasks, new Date(projectStartDate), companyCalendar, ptoByGardenerId);
			}
			setTimelineTasks(tasks);
			if (options?.markManual !== false) {
				setTimelineManuallyEdited(true);
			}
			if (options?.clearReorderNotice) {
				setTimelineReorderNotice(false);
			}
			if (options?.syncPlan !== false && displayedAiContent && tasks.length > 0) {
				const synced = syncAiContentFromTimeline(displayedAiContent, tasks, language);
				setOfferPlanContent((prev) => {
					const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent));
					return { ...base, ...synced };
				});
				const summary = timelineSummaryTextFromPlanInstructions(synced.instructions, language);
				if (summary) setTimelineEstimate(summary);
			}
		},
		[
			projectStartDate,
			selectedGardeners,
			availableGardeners,
			companyCalendar,
			ptoByGardenerId,
			displayedAiContent,
			language
		]
	);

	/** Task list order matches execution sequence (same as timeline state after scheduling). */
	const timelineTasksInExecutionOrder = timelineTasks;

	// Re-apply company working days once schedule loads (fixes timeline built before settings arrived)
	useEffect(() => {
		if (!companySchedule || !projectStartDate || timelineManuallyEdited || timelineTasks.length === 0) {
			return;
		}
		const next = scheduleTimelineTasksSequentially(
			timelineTasks,
			new Date(projectStartDate),
			companyCalendar
		);
		commitTimelineTasks(next, { syncPlan: true, markManual: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run when company schedule loads
	}, [companySchedule]);

	const handleMoveTimelineTask = useCallback(
		(sortedIndex: number, direction: 'up' | 'down') => {
			if (!projectStartDate) return;
			const ordered = [...timelineTasksInExecutionOrder];
			const reordered = moveTimelineTaskInOrder(
				ordered,
				sortedIndex,
				direction,
				new Date(projectStartDate),
				companyCalendar ?? undefined
			);
			commitTimelineTasks(reordered, { syncPlan: true, markManual: true });
			setTimelineReorderNotice(true);
		},
		[timelineTasksInExecutionOrder, projectStartDate, companyCalendar, commitTimelineTasks]
	);

	const timelineAssignedCount = useMemo(
		() => timelineTasks.filter(taskHasAssignedGardeners).length,
		[timelineTasks]
	);

	const teamGardenersForTaskAssign = useMemo(
		() =>
			selectedGardeners
				.map((id) => availableGardeners.find((g) => g.id === id))
				.filter((g): g is GardenerResponse => !!g),
		[selectedGardeners, availableGardeners]
	);

	const runAutoAssignTimelineGardeners = useCallback(
		(onlyUnassigned: boolean) => {
			if (timelineTasks.length === 0 || availableGardeners.length === 0) return;
			if (selectedGardeners.length === 0) return;
			if (!projectStartDate) return;
			setIsAutoAssigningTimeline(true);
			try {
				const availabilityById = new Map<number, boolean>();
				availabilityResults.forEach((r) => {
					const id = Number(r.gardener_id);
					if (!Number.isNaN(id)) availabilityById.set(id, !!r.available);
				});

				const candidates = selectedGardeners
					.map((id) => {
						const base = availableGardeners.find((g) => g.id === id);
						if (!base) return null;
						const skills = gardenerSkillsOverrides[id] ?? base.skills ?? [];
						const available = availabilityById.has(id) ? availabilityById.get(id)! : true;
						return { id, name: base.display_name || base.name || base.email || String(id), skills, available };
					})
					.filter((c): c is { id: number; name: string; skills: string[]; available: boolean } => !!c);

				if (candidates.length === 0) return;

				const assigned = autoAssignGardenersToTimelineTasks(
					timelineTasks,
					candidates,
					skillsNeededForPlan,
					new Date(projectStartDate),
					companyCalendar,
					hasGardenerDateConflict,
					planRecommendedTeamSize,
					{ onlyUnassigned }
				);
				commitTimelineTasks(assigned, { syncPlan: true, markManual: true, clearReorderNotice: true });
				setShowTimelineDateEditHint(false);
			} finally {
				setIsAutoAssigningTimeline(false);
			}
		},
		[
			timelineTasks,
			availableGardeners,
			selectedGardeners,
			projectStartDate,
			availabilityResults,
			gardenerSkillsOverrides,
			skillsNeededForPlan,
			planRecommendedTeamSize,
			companyCalendar,
			hasGardenerDateConflict,
			commitTimelineTasks
		]
	);

	const handleAutoAssignButtonClick = useCallback(() => {
		if (timelineTasks.length === 0 || selectedGardeners.length === 0) return;
		const total = timelineTasks.length;
		const assigned = timelineAssignedCount;
		if (assigned === 0 || assigned === total) {
			runAutoAssignTimelineGardeners(false);
			return;
		}
		setAutoAssignChoiceOpen(true);
	}, [timelineTasks.length, selectedGardeners.length, timelineAssignedCount, runAutoAssignTimelineGardeners]);

	const handleSetTaskGardeners = useCallback(
		(taskId: string, gardenerIds: string[]) => {
			const allowed = new Set(selectedGardeners.map(String));
			const ids = gardenerIds.filter((id) => allowed.has(id));
			const names = ids.map((id) => {
				const g = availableGardeners.find((x) => String(x.id) === id);
				return g?.display_name || g?.name || g?.email || id;
			});
			const next = timelineTasks.map((task) => {
				if (task.id !== taskId) return task;
				if (ids.length === 0) {
					return {
						...task,
						gardenerId: undefined,
						gardenerName: undefined,
						gardenerIds: undefined,
						gardenerNames: undefined
					};
				}
				return {
					...task,
					gardenerIds: ids,
					gardenerNames: names,
					gardenerId: ids[0],
					gardenerName: names[0]
				};
			});
			commitTimelineTasks(next, { syncPlan: true, markManual: true });
		},
		[selectedGardeners, availableGardeners, timelineTasks, commitTimelineTasks]
	);

	useEffect(() => {
		setTimelineManuallyEdited(false);
		setTimelineReorderNotice(false);
	}, [viewingVersionKey]);

	useEffect(() => {
		if (!timelineManuallyEdited || !projectStartDate || timelineTasks.length === 0) return;
		const rescheduled = scheduleTimelineTasksSequentially(
			timelineTasks,
			new Date(projectStartDate),
			companyCalendar ?? undefined
		);
		commitTimelineTasks(rescheduled, { syncPlan: true, markManual: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only when project start date changes
	}, [projectStartDate]);

	// Generate timeline from plan implementation instructions when available; otherwise use synthetic (budget-based) timeline.
	// Uses company working days when set; assigns selected gardeners by skills; extends task dates to skip assignee PTO.
	useEffect(() => {
		if (plan?.questionnaire?.projectStartDate && !hasTouchedProjectStartDate) {
			setProjectStartDate(plan.questionnaire.projectStartDate);
		}
		if (plan?.questionnaire?.projectStartDate && !hasTouchedValidUntil) {
			const preferred = new Date(plan.questionnaire.projectStartDate);
			const defaultValid = new Date(preferred);
			defaultValid.setDate(defaultValid.getDate() - 7);
			setValidUntil(defaultValid.toISOString().split('T')[0]);
		}
		if (!plan || !projectStartDate) return;
		const startDate = new Date(projectStartDate);
		if (timelineManuallyEdited) {
			if (timelineTasks.length > 0 && selectedGardeners.length > 0 && availableGardeners.length > 0) {
				setTimelineTasks((prev) =>
					adjustTaskDatesForPTO(prev, startDate, companyCalendar, ptoByGardenerId)
				);
			}
			return;
		}
		const instructions = displayedAiContent?.instructions ?? plan.aiGenerated?.instructions;
		const structuredTimelineTasks = displayedAiContent?.timeline_tasks ?? (plan.aiGenerated as any)?.timeline_tasks;
		const instructionMeta = displayedAiContent?.instruction_metadata;
		let tasks: GanttTask[];

		const structuredTasks = ganttTasksFromStructuredTimeline(
			Array.isArray(structuredTimelineTasks) ? structuredTimelineTasks : [],
			startDate,
			companyCalendar ?? undefined
		);
		if (structuredTasks.length > 0) {
			tasks = structuredTasks;
			setTimelineEstimate(prev => {
				const defaultText = instructions?.length
					? timelineSummaryTextFromPlanInstructions(instructions, language)
					: '';
				return defaultText && isDefaultOfferTimelineEstimate(prev) ? defaultText : prev;
			});
		} else if (instructions?.length) {
			const planTasks = ganttTasksFromPlanInstructions(instructions, startDate, companyCalendar ?? undefined);
			if (planTasks.length > 0) {
				tasks = planTasks;
				setTimelineEstimate(prev => {
					const defaultText = timelineSummaryTextFromPlanInstructions(instructions, language);
					return defaultText && isDefaultOfferTimelineEstimate(prev) ? defaultText : prev;
				});
			} else {
				tasks = estimatedPrice ? generateSyntheticGanttData('garden', estimatedPrice, startDate, undefined, companyCalendar ?? undefined, language) : [];
			}
		} else {
			tasks = estimatedPrice ? generateSyntheticGanttData('garden', estimatedPrice, startDate, undefined, companyCalendar ?? undefined, language) : [];
		}

		// AI-suggested skills & team size per task:
		// - requiredSkills: per-step instruction_metadata mapped to known skill keys; fallback to planRequiredSkills
		// - requiredGardeners: from per-step instruction_metadata when available, otherwise default 1
		const planRequiredSkills = skillsNeededForPlan;

		if (tasks.length > 0) {
			tasks = tasks.map((task, idx) => {
				const metaForStep = Array.isArray(instructionMeta) ? instructionMeta[idx] : undefined;
				const stepSkills = normalizeSkillsToOptions(metaForStep?.required_skills_for_step);
				const stepTeamSize = metaForStep?.required_gardeners_for_step;
				const normalizedPlanSkills = normalizeSkillsToOptions(planRequiredSkills);
				const resolvedTeam =
					typeof stepTeamSize === 'number' && stepTeamSize > 0
						? stepTeamSize
						: typeof task.requiredGardeners === 'number' && task.requiredGardeners > 0
							? task.requiredGardeners
							: resolveTaskRequiredGardeners(task, planRecommendedTeamSize);
				return {
					...task,
					requiredSkills: stepSkills.length > 0
						? stepSkills
						: (normalizedPlanSkills.length > 0 ? normalizedPlanSkills : undefined),
					requiredGardeners: resolvedTeam
				};
			});
		}

		if (tasks.length > 0) {
			tasks = scheduleTimelineTasksSequentially(tasks, startDate, companyCalendar);
		}

		if (tasks.length > 0 && selectedGardeners.length > 0 && availableGardeners.length > 0) {
			// Only adjust for PTO; actual assignment happens when user clicks "Auto-assign gardeners"
			tasks = adjustTaskDatesForPTO(tasks, startDate, companyCalendar, ptoByGardenerId);
		}

		setTimelineTasks(tasks);
	}, [plan, estimatedPrice, projectStartDate, displayedAiContent, companyCalendar, selectedGardeners, availableGardeners, ptoByGardenerId, skillsNeededForPlan, planRecommendedTeamSize, timelineManuallyEdited, timelineTasks.length, language]);
	
	// Check availability when gardeners or timeline changes
	useEffect(() => {
		if (availableGardeners.length === 0 || !projectStartDate || timelineTasks.length === 0) {
			setAvailabilityResults([]);
			return;
		}
		
		const checkAvailability = async () => {
			// Calculate end date from timeline tasks
			const endDate = timelineTasks.reduce((latest, task) => {
				const taskEnd = new Date(task.endDate);
				return taskEnd > latest ? taskEnd : latest;
			}, new Date(projectStartDate));
			
			setIsCheckingAvailability(true);
			try {
				const result = await checkMultipleGardenersAvailability({
					gardener_ids: availableGardeners.map(g => g.id),
					start_date: projectStartDate,
					end_date: endDate.toISOString().split('T')[0]
				});
				setAvailabilityResults(result.results);
			} catch (err) {
				console.error('Failed to check availability:', err);
			} finally {
				setIsCheckingAvailability(false);
			}
		};
		
		// Debounce availability check
		const timeoutId = setTimeout(checkAvailability, 500);
		return () => clearTimeout(timeoutId);
	}, [availableGardeners, projectStartDate, timelineTasks]);

	useEffect(() => {
		if (!user?.id || !planId) return;
		if (user.role === 'company' || user.role === 'admin') {
			setOfferFlowAllowed(true);
			return;
		}
		if (user.role !== 'gardener') {
			setOfferFlowAllowed(true);
			return;
		}
		let cancelled = false;
		setOfferFlowAllowed(null);
		getMyOffersPermission(String(companyActorId ?? user.id))
			.then((r) => {
				if (cancelled) return;
				const ok = r.can_send_marketplace_offers;
				setOfferFlowAllowed(ok);
				if (!ok) {
					navigate(`/app/seeker/plan/${planId}`, { replace: true });
				}
			})
			.catch(() => {
				if (cancelled) return;
				setOfferFlowAllowed(false);
				navigate(`/app/seeker/plan/${planId}`, { replace: true });
			});
		return () => {
			cancelled = true;
		};
	}, [user?.id, user?.role, planId, navigate, companyActorId]);

	useEffect(() => {
		if (!planId) return;

		const loadPlan = async () => {
			let loadedPlan: PublicPlan | undefined;
			// Prefer API when planId looks like database ID so we get latest + revisions
			if (planId && (planId.startsWith('db-') || !isNaN(parseInt(planId, 10)))) {
				try {
					const { getPublicPlanByIdFromAPI } = await import('@data/publicPlans');
					const apiPlan = await getPublicPlanByIdFromAPI(planId);
					if (apiPlan) {
						loadedPlan = apiPlan;
					}
				} catch (err) {
					console.error('Failed to load plan from API:', err);
				}
			}
			if (!loadedPlan) {
				loadedPlan = planId ? getPublicPlanById(planId) : undefined;
			}
			
			if (loadedPlan) {
				setPlan(loadedPlan);
				// Pre-fill estimated price from plan's itemized pricing breakdown
				const pb = loadedPlan.aiGenerated?.pricingBreakdown;
				if (pb) {
					setEstimatedPrice(normalizePricingBreakdown(pb).mid);
				} else {
					const range = loadedPlan.questionnaire?.budgetRange;
					if (range && typeof range.min === 'number' && typeof range.max === 'number') {
						setEstimatedPrice(Math.round((range.min + range.max) / 2));
					}
				}
			} else {
				console.warn('[QuoteWizard] Plan not found:', planId);
			}
		};
		
		loadPlan();

		const handleUpdate = () => {
			const updated = getPublicPlanById(planId);
			if (updated) {
				setPlan(updated);
			}
		};

		window.addEventListener('publicPlansUpdated', handleUpdate);
		return () => {
			window.removeEventListener('publicPlansUpdated', handleUpdate);
		};
	}, [planId]);

	useEffect(() => {
		if (!isCompanyDirectFlow || !plan?.databaseId || companyActorId == null) {
			setCompanyInviteStatus(null);
			return;
		}
		let cancelled = false;
		getCompanyInviteForPlan(plan.databaseId, companyActorId)
			.then((res) => {
				if (!cancelled) setCompanyInviteStatus(res);
			})
			.catch(() => {
				if (!cancelled) setCompanyInviteStatus(null);
			});
		return () => {
			cancelled = true;
		};
	}, [isCompanyDirectFlow, plan?.databaseId, companyActorId]);

	useEffect(() => {
		if (!plan?.databaseId || companyActorId == null) {
			setExistingOfferId(null);
			return;
		}
		let cancelled = false;
		getOffersForPlan(plan.databaseId)
			.then((list) => {
				if (cancelled) return;
				const mine = list
					.filter(
						(o) =>
							Number(o.gardener_user_id) === companyActorId &&
							(o.status === 'draft' || o.status === 'submitted')
					)
					.sort(
						(a, b) =>
							new Date(b.updated_at || b.created_at).getTime() -
							new Date(a.updated_at || a.created_at).getTime()
					)[0];
				setExistingOfferId(mine ? Number(mine.id) : null);
			})
			.catch(() => {
				if (!cancelled) setExistingOfferId(null);
			});
		return () => {
			cancelled = true;
		};
	}, [plan?.databaseId, companyActorId]);

	async function handleRegenerateWithChanges() {
		if (!plan?.questionnaire) return;
		const originalBeforeUrl = plan.questionnaire.photoBeforeUrl || plan.photoBeforeUrl;
		const latestAfterUrl = displayedAiContent?.photoAfterUrl || plan.aiGenerated?.photoAfterUrl;
		if (!originalBeforeUrl) {
			showAlert('This plan has no before photo; regeneration is not available.', { title: 'Cannot regenerate' });
			return;
		}
		const instructions = editInstructions.trim();
		if (!instructions) return;
		setIsRegenerating(true);
		try {
			const newContent = await generateAIPlan(
				{ ...plan.questionnaire, photoBeforeUrl: originalBeforeUrl },
				{
					editInstructions: instructions,
					editBaseImageUrl: latestAfterUrl || originalBeforeUrl,
					originalBeforeImageUrl: originalBeforeUrl
				}
			);
			// Store only in offer state; do not update the marketplace plan
			setOfferRevisions((prev) => [...prev, newContent]);
			setOfferPlanContent(newContent);
			setTimelineManuallyEdited(false);
			setTimelineReorderNotice(false);
			setEditModalOpen(false);
			setEditInstructions('');
			setViewingVersionKey('current');
		} catch (err) {
			console.error('Regenerate failed:', err);
			showAlert(err instanceof Error ? err.message : 'Failed to regenerate. Please try again.', { title: 'Error' });
		} finally {
			setIsRegenerating(false);
		}
	}

	function handleUseVersionForOffer(versionKey: string) {
		if (!plan?.aiGenerated) return;
		let versionContent: AIGeneratedContent | null = null;
		if (versionKey === 'current') {
			versionContent = offerPlanContent ?? plan.aiGenerated;
		} else if (versionKey.startsWith('plan-')) {
			const revisionIndex = Number(versionKey.replace('plan-', ''));
			if (!Number.isNaN(revisionIndex) && plan.revisions?.[revisionIndex]) {
				versionContent = { ...plan.aiGenerated, ...plan.revisions[revisionIndex] };
			}
		} else if (versionKey.startsWith('offer-')) {
			const revisionIndex = Number(versionKey.replace('offer-', ''));
			if (!Number.isNaN(revisionIndex) && offerRevisions[revisionIndex]) {
				versionContent = offerRevisions[revisionIndex];
			}
		}
		if (!versionContent) return;
		setOfferPlanContent(versionContent);
		setViewingVersionKey('current');
	}

	// Minimum price = materials + labor (floor for estimated price)
	const materialsPlusLaborTotal = useMemo(
		() => getMaterialsPlusLaborTotal(displayedAiContent?.pricingBreakdown),
		[displayedAiContent?.pricingBreakdown]
	);

	const validationError = useMemo(() => {
		if (!plan) return 'Plan not found.';
		if (Number.isNaN(estimatedPrice) || estimatedPrice <= 0) return 'Please enter a positive estimated price.';
		if (estimatedPrice < materialsPlusLaborTotal) return `Estimated price cannot be below materials + labor (${materialsPlusLaborTotal.toLocaleString()} €).`;
		if (!timelineEstimate.trim()) return 'Share a timeline or production estimate.';
		return null;
	}, [plan, estimatedPrice, materialsPlusLaborTotal, timelineEstimate]);

	async function handleSendOffer() {
		if (isSendingRef.current) return;
		if (validationError) {
			showAlert(validationError, { title: 'Please review your inputs' });
			return;
		}
		if (!plan) return;

		isSendingRef.current = true;
		setIsSending(true);
		try {
			if (!user) {
				showAlert('You must be logged in to send an offer.', { title: 'Authentication required' });
				return;
			}
			if (companyActorId == null) {
				showAlert(
					'Use “View as user…” in the admin bar and select a company or gardener account to send an offer.',
					{ title: 'Select company or gardener' }
				);
				return;
			}

			// Check if plan has a database ID (preferred) or if planId itself is numeric.
			// Coerce to number: API/DB often return ids as strings (e.g. "85").
			const planIdNum = Number(plan.databaseId) || parseInt(String(plan.id).replace(/^db-/, ''), 10);
			const isNumericPlanId = Number.isInteger(planIdNum) && planIdNum > 0;

			// Company / gardener user id (respects admin “View as…”)
			const userIdNum = companyActorId;
			const isNumericUserId = Number.isInteger(userIdNum) && userIdNum > 0;
			
			if (isNumericPlanId && isNumericUserId) {
				// Use backend API
				try {
					// Build plan_snapshot so the offer stores exactly what we're proposing (marketplace plan stays unchanged)
					const planSnapshot = plan && displayedAiContent ? {
						styleKey: plan.styleKey,
						city: plan.city,
						title: plan.title,
						questionnaire: plan.questionnaire
							? {
								gardenSize: plan.questionnaire.gardenSize,
								location: plan.questionnaire.location,
								projectFocus: plan.questionnaire.projectFocus,
								photoBeforeUrl: plan.questionnaire.photoBeforeUrl ?? plan.photoBeforeUrl ?? undefined
							}
							: undefined,
						photoBeforeUrl: plan.questionnaire?.photoBeforeUrl ?? plan.photoBeforeUrl ?? undefined,
						aiGenerated: JSON.parse(JSON.stringify(displayedAiContent))
					} : undefined;

					const offerPayload = {
						gardener_user_id: userIdNum,
						price_min: materialsPlusLaborTotal,
						price_mid: Math.round(estimatedPrice),
						price_max: Math.round(estimatedPrice),
						price_final: Math.round(estimatedPrice),
						proposal_text: notes.trim() || undefined,
						proposal_summary: gardenerName.trim() || undefined,
						estimated_timeline: timelineEstimate.trim() || undefined,
						project_start_date: projectStartDate || undefined,
						valid_until: validUntil || undefined,
						public_notes: notes.trim() || undefined,
						status: 'submitted' as const,
						vat_rate: 19.00,
						currency: 'EUR',
						timeline_data: timelineTasks.length > 0 ? timelineTasks : undefined,
						plan_snapshot: planSnapshot,
						selected_gardeners: selectedGardeners.length > 0 ? selectedGardeners.map(id => {
							const skillsForOffer = gardenerSkillsOverrides[id] ?? availableGardeners.find(g => g.id === id)?.skills ?? [];
							return skillsForOffer.length > 0 ? { id, skills: skillsForOffer } : { id };
						}) : undefined
					};

					const backendOffer =
						existingOfferId != null && existingOfferId > 0
							? await updateOffer(existingOfferId, offerPayload)
							: await createOffer(planIdNum, offerPayload);
					setExistingOfferId(Number(backendOffer.id));
					

					if (plan.status === 'draft' && plan.databaseId) {
						try {
							const { updatePlanStatus } = await import('@services/plans');
							await updatePlanStatus(plan.databaseId, 'published');
							setPlan((prev) => (prev ? { ...prev, status: 'active' } : prev));
						} catch (publishErr) {
							console.warn('Quote saved but plan status could not be updated:', publishErr);
						}
					}

					const offerIdNum = Number(backendOffer.id);
					const notifyClientId =
						linkedClientUserId != null && linkedClientUserId > 0
							? String(linkedClientUserId)
							: plan.seekerUserId && String(plan.seekerUserId) !== String(userIdNum)
								? plan.seekerUserId
								: null;

					if (isCompanyDirectFlow && !clientIsLinked && plan.databaseId) {
						setShareOfferId(Number.isFinite(offerIdNum) ? offerIdNum : null);
						setShareClientModalOpen(true);
					} else {
						if (notifyClientId) {
							notifyOfferReceived(notifyClientId, String(backendOffer.id), plan.id, gardenerName.trim(), plan.title).catch(
								(err) => {
									console.error('Failed to send notification:', err);
								}
							);
						}
						const offerSentMessage = isCompanyDirectFlow
							? copy(
									'Your client can review this quote in their Offers.',
									'Ihr Kunde kann dieses Angebot unter „Angebote“ einsehen.',
									'Vaš klijent može pregledati ovu ponudu u svojim Ponudama.'
								)
							: copy(
									'The seeker will see it on their Offers page.',
									'Die suchende Person sieht es unter Angebote.',
									'Tražitelj će je vidjeti na stranici Ponude.'
								);
						showAlert(offerSentMessage, {
							title: isCompanyDirectFlow
								? copy('Quote delivered', 'Angebot zugestellt', 'Ponuda dostavljena')
								: copy('Offer sent', 'Angebot gesendet', 'Ponuda poslana')
						});
					}
					setNotes('');
					setTimelineEstimate(
						plan.aiGenerated?.instructions?.length
							? (timelineSummaryTextFromPlanInstructions(plan.aiGenerated.instructions, language) || defaultTimelineFallback)
							: defaultTimelineFallback
					);
					
					// Trigger a reload event for the offers page
					window.dispatchEvent(new CustomEvent('offersUpdated'));
					return;
				} catch (apiErr) {
					console.error('Failed to create offer via API:', apiErr);
					console.error('API Error details:', {
						message: apiErr instanceof Error ? apiErr.message : String(apiErr),
						planId: planIdNum,
						userId: userIdNum
					});
					
					// Show error and stop — do not fall back to localStorage (offer would not appear for seeker)
					const msg = apiErr instanceof Error ? apiErr.message : String(apiErr);
					showAlert(`Offer could not be saved: ${msg}. Please try again or contact support if it persists.`, { 
						title: 'Could not send offer'
					});
					return;
				}
			} else {
				console.warn('Plan or User ID is not numeric, using localStorage:', {
					planId: plan.id,
					planIdType: typeof plan.id,
					userId: companyActorId,
					message: 'To save offers to the database, plans and users must be created in the database first.'
				});
			}

			// Generate timeline data
			const timelineSummary = generateTimelineSummary('garden', estimatedPrice, new Date(projectStartDate));
			const ganttData = generateSyntheticGanttData('garden', estimatedPrice, new Date(projectStartDate), undefined, undefined, language);

			// Fallback to localStorage (for string planIds or if API fails)
			const offer: Offer = {
				id: `o${Math.random().toString(36).slice(2, 9)}`,
				planId: plan.id,
				gardenerId: String(companyActorId ?? user.id),
				gardenerName: gardenerName.trim(),
				bands: {
					min: materialsPlusLaborTotal,
					mid: Math.round(estimatedPrice),
					max: Math.round(estimatedPrice)
				},
				status: 'pending',
				createdAt: new Date().toISOString(),
				moderationStatus: 'active',
				message: notes.trim(),
				timelineEstimate: timelineEstimate.trim(),
				projectStartDate: projectStartDate,
				validUntil: validUntil || undefined,
				timelineSummary,
				ganttData
			};

			addOffer(offer);
			
			// Notify the seeker about the new offer
			if (plan.seekerUserId) {
				notifyOfferReceived(plan.seekerUserId, offer.id, plan.id, gardenerName.trim(), plan.title).catch(err => {
					console.error('Failed to send notification:', err);
				});
			}
			
			showAlert('Offer sent! The seeker will see it on their Offers page.', { title: 'Offer sent' });
			setNotes('');
			setTimelineEstimate(
				plan.aiGenerated?.instructions?.length
					? (timelineSummaryTextFromPlanInstructions(plan.aiGenerated.instructions, language) || defaultTimelineFallback)
					: defaultTimelineFallback
			);
			
			// Trigger a reload event for the offers page
			window.dispatchEvent(new CustomEvent('offersUpdated'));
		} catch (err) {
			console.error('Failed to send offer', err);
			showAlert('Sending the offer failed. Please try again.', { title: 'Something went wrong' });
		} finally {
			isSendingRef.current = false;
			setIsSending(false);
		}
	}

	async function handleDownloadPDF() {
		if (!plan || !plan.aiGenerated || !plan.questionnaire) {
			showAlert('This plan does not have complete data for PDF generation.', { title: 'Error' });
			return;
		}

		try {
			await generatePlanPDF({
				plan,
				aiContent: plan.aiGenerated,
				questionnaire: plan.questionnaire,
				language
			});
		} catch (error) {
			console.error('Failed to generate PDF:', error);
			showAlert('Failed to generate PDF. Please try again.', { title: 'Error' });
		}
	}

	async function handleReportSeekerFromOfferFlow() {
		if (!plan) return;
		const reporterUserId = user?.id ? parseInt(String(user.id), 10) : 0;
		const targetUserId = plan.seekerUserId ? parseInt(String(plan.seekerUserId), 10) : 0;
		if (!reporterUserId || Number.isNaN(reporterUserId)) {
			window.alert(copy('Please log in to report users.', 'Bitte melden Sie sich an, um Nutzer zu melden.', 'Prijavite se kako biste prijavili korisnike.'));
			return;
		}
		if (!targetUserId || Number.isNaN(targetUserId)) {
			window.alert(copy('This plan cannot be reported yet.', 'Dieser Plan kann derzeit nicht gemeldet werden.', 'Ovaj plan se trenutno ne može prijaviti.'));
			return;
		}
		const details = window.prompt(
			copy(
				'Optional: tell us why you are reporting this user.',
				'Optional: Warum melden Sie diesen Nutzer?',
				'Neobavezno: recite zašto prijavljujete ovog korisnika.'
			),
			''
		);
		setIsReportingUser(true);
		try {
			await createIssue({
				reporter_user_id: reporterUserId,
				type: 'user_report',
				category: 'user',
				target_type: 'user',
				target_id: targetUserId,
				reason_code: 'other',
				message: details?.trim() || `Reported from offer flow for plan ${plan.title}`
			});
			window.alert(copy('Report submitted. Thank you.', 'Meldung gesendet. Danke.', 'Prijava je poslana. Hvala.'));
		} catch (err: any) {
			window.alert(err?.message || copy('Failed to submit report.', 'Meldung konnte nicht gesendet werden.', 'Slanje prijave nije uspjelo.'));
		} finally {
			setIsReportingUser(false);
		}
	}

	if (user?.role === 'gardener' && offerFlowAllowed === null) {
		return (
			<div className="text-center py-5">
				<div className="spinner-border text-success" role="status">
					<span className="visually-hidden">{copy('Loading…', 'Wird geladen…', 'Učitavanje…')}</span>
				</div>
			</div>
		);
	}

	if (user?.role === 'gardener' && offerFlowAllowed === false) {
		return null;
	}

	if (!plan) {
		return (
			<>
				<div className="d-flex flex-wrap align-items-center gap-3 mb-3">
					<BackButton fallbackPath="/app/plans" size="sm" />
					<h1 className="h3 mb-0">{copy('Plan Offer Workspace', 'Plan-Angebotsbereich', 'Radni prostor ponude plana')}</h1>
				</div>
				<div className="alert alert-warning">{copy('This plan is no longer available.', 'Dieser Plan ist nicht mehr verfügbar.', 'Ovaj plan više nije dostupan.')}</div>
			</>
		);
	}

	return (
		<div className="offer-wizard-refresh">
			<style>{`
				.offer-wizard-refresh {
					--ow-border: #d8e2dc;
					--ow-soft: #f4f8f5;
					--ow-text-soft: #5f6f67;
				}
				.offer-wizard-refresh > .card {
					border: 1px solid var(--ow-border);
					border-radius: 14px;
					box-shadow: 0 8px 28px rgba(20, 40, 28, 0.06);
				}
				.offer-wizard-refresh .card-body {
					padding: 1.25rem;
				}
				.offer-wizard-refresh .form-label {
					font-weight: 600;
					margin-bottom: 0.4rem;
				}
				.offer-wizard-refresh .form-control,
				.offer-wizard-refresh .form-select,
				.offer-wizard-refresh .input-group-text {
					border-radius: 10px;
					border-color: #d5dfd8;
				}
				.offer-wizard-refresh .form-text {
					color: var(--ow-text-soft);
				}
				.offer-wizard-refresh details {
					background: #fbfdfb;
					border: 1px solid #e3ebe5;
					border-radius: 10px;
					padding: 0.7rem 0.85rem;
				}
				.offer-wizard-refresh .btn.btn-success {
					border-radius: 10px;
					box-shadow: 0 4px 14px rgba(25, 135, 84, 0.2);
				}
				.offer-wizard-refresh .btn.btn-outline-secondary,
				.offer-wizard-refresh .btn.btn-outline-danger {
					border-radius: 10px;
				}
				.offer-wizard-refresh .offer-top-meta {
					background: var(--ow-soft);
					border: 1px solid var(--ow-border);
					border-radius: 12px;
					padding: 0.8rem 0.9rem;
				}
				.offer-wizard-refresh .offer-top-meta .meta-item {
					min-width: 180px;
				}
				.offer-wizard-refresh .offer-top-meta .meta-label {
					font-size: 0.72rem;
					letter-spacing: 0.05em;
					color: #6f7f76;
					text-transform: uppercase;
				}
				.offer-wizard-refresh .offer-top-meta .meta-value {
					font-weight: 600;
				}
			`}</style>
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
				<BackButton
					fallbackPath={
						offerReturnTo ??
						(user?.role === 'company' && plan?.id
							? `/app/seeker/plan/${plan.id}`
							: '/app/plans')
					}
					label={
						offerReturnTo === '/app/seeker/generate-plan'
							? copy('Back to plan review', 'Zurück zur Planprüfung', 'Natrag na pregled plana')
							: user?.role === 'company'
								? copy('Back to plan', 'Zurück zum Plan', 'Natrag na plan')
								: copy('Back to plans', 'Zurück zu Plänen', 'Natrag na planove')
					}
					size="sm"
					forceFallback={Boolean(offerReturnTo) || user?.role === 'company'}
				/>
				<h1 className="h3 mb-0">{copy('Plan Offer Workspace', 'Plan-Angebotsbereich', 'Radni prostor ponude plana')}</h1>
			</div>
			<div className="card">
				<div className="card-body">
					<div className="mb-3">
						<div className="text-muted small">{copy('Plan', 'Plan', 'Plan')}</div>
						<div className="fw-medium">{plan.title}</div>
						{plan.city && (
							<div className="text-muted small">{plan.city}</div>
						)}
					</div>
					<div className="row g-3">
						<div className="col-12">
							{gardenerName ? (
								<div className="mb-3 text-muted small">
									{copy('Sending offer as:', 'Sende Angebot als:', 'Slanje ponude kao:')} <strong className="text-body">{gardenerName}</strong>
								</div>
							) : null}
							<div className="offer-top-meta d-flex flex-wrap gap-3 mb-3">
								<div className="meta-item">
									<div className="meta-label">{copy('Seeker budget', 'Budget des Suchenden', 'Budžet vlasnika')}</div>
									<div className="meta-value">{seekerBudget}</div>
								</div>
								<div className="meta-item">
									<div className="meta-label">{copy('Requested timeline', 'Gewünschter Zeitplan', 'Traženi vremenski okvir')}</div>
									<div className="meta-value">{seekerTimeline}</div>
								</div>
								<div className="meta-item">
									<div className="meta-label">{copy('Estimated offer', 'Geschätztes Angebot', 'Procijenjena ponuda')}</div>
									<div className="meta-value">{estimatedPrice.toLocaleString()} EUR</div>
								</div>
							</div>
							{plan.questionnaire?.selectedComponents && plan.questionnaire.selectedComponents.length > 0 && (
								<div className="alert alert-success-subtle border border-success-subtle mb-3">
									<div className="text-success-emphasis fw-semibold small mb-2">
										<i className="bi bi-grid-1x2-fill me-1"></i>
										{copy('Requested components', 'Gewünschte Komponenten', 'Tražene komponente')}
									</div>
									<div className="d-flex flex-wrap gap-2">
										{plan.questionnaire.selectedComponents.map((componentId) => (
											<span key={componentId} className="badge bg-success text-white">
												{getComponentLabel(componentId, language)}
											</span>
										))}
									</div>
								</div>
							)}

							{/* Seeker questionnaire inputs (answered-only) */}
							{plan.questionnaire && (
								<details className="mb-4">
									<summary style={{ cursor: 'pointer', fontWeight: 700 }}>
										{copy('Seeker questionnaire (answered)', 'Fragebogen des Suchenden (beantwortet)', 'Upitnik vlasnika (odgovoreno)')}
									</summary>
									<div className="mt-3">
										<div className="row g-3">
											{plan.questionnaire.gardenSize > 0 && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Garden size', 'Gartengröße', 'Veličina vrta')}</div>
													<div className="fw-semibold">{plan.questionnaire.gardenSize} m²</div>
												</div>
											)}
											{plan.questionnaire.gardenShape && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Garden shape', 'Gartenform', 'Oblik vrta')}</div>
													<div className="fw-semibold">{plan.questionnaire.gardenShape}</div>
												</div>
											)}
											{plan.questionnaire.location && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Location', 'Standort', 'Lokacija')}</div>
													<div className="fw-semibold">{plan.questionnaire.location}</div>
												</div>
											)}
											{plan.questionnaire.projectFocus && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Project focus', 'Projektfokus', 'Fokus projekta')}</div>
													<div className="fw-semibold">{localizeProjectFocus(plan.questionnaire.projectFocus)}</div>
												</div>
											)}

											{plan.questionnaire.selectedComponents && plan.questionnaire.selectedComponents.length > 0 && (
												<div className="col-12">
													<div className="text-muted small mb-1">{copy('Garden components', 'Gartenkomponenten', 'Komponente vrta')}</div>
													<div className="d-flex flex-wrap gap-2">
														{plan.questionnaire.selectedComponents.map((componentId) => (
															<span key={componentId} className="badge bg-success text-white">
																<i className="bi bi-check-circle me-1" />
																{getComponentLabel(componentId, language)}
															</span>
														))}
													</div>
												</div>
											)}

											{plan.questionnaire.needsWants && plan.questionnaire.needsWants.trim().length > 0 && (
												<div className="col-12">
													<div className="text-muted small mb-1">{copy('Needs & wants', 'Bedürfnisse & Wünsche', 'Potrebe i želje')}</div>
													<div className="text-break">{plan.questionnaire.needsWants}</div>
												</div>
											)}

											{plan.questionnaire.maintenanceLevel && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Maintenance level', 'Pflegeaufwand', 'Razina održavanja')}</div>
													<div className="fw-semibold text-capitalize">{plan.questionnaire.maintenanceLevel}</div>
												</div>
											)}
											{plan.questionnaire.plantDensityPreference && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Plant density', 'Pflanzdichte', 'Gustoća sadnje')}</div>
													<div className="fw-semibold text-capitalize">{plan.questionnaire.plantDensityPreference}</div>
												</div>
											)}

											{plan.questionnaire.gardenUses && plan.questionnaire.gardenUses.length > 0 && (
												<div className="col-12">
													<div className="text-muted small mb-1">{copy('How you’ll use the garden', 'Wie Sie den Garten nutzen', 'Kako ćete koristiti vrt')}</div>
													<div className="d-flex flex-wrap gap-2">
														{plan.questionnaire.gardenUses.map((useKey) => {
															const labelMap: Record<string, string> = {
																relaxing: copy('Relaxing / quiet time', 'Entspannung / Ruhezeit', 'Opuštanje / mir'),
																growing_food: copy('Growing food', 'Lebensmittel anbauen', 'Uzgoj hrane'),
																kids_pets: copy('Kids & pets', 'Kinder & Haustiere', 'Djeca i kućni ljubimci'),
																hosting: copy('Hosting', 'Gäste empfangen', 'Druženje'),
																dining_outdoors: copy('Outdoor dining', 'Essen im Freien', 'Blagovanje na otvorenom')
															};
															return (
																<span key={useKey} className="badge bg-secondary text-white">
																	{labelMap[useKey] ?? useKey}
																</span>
															);
														})}
													</div>
												</div>
											)}

											{plan.questionnaire.hostingMaxPeople != null && plan.questionnaire.hostingMaxPeople > 0 && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Hosting (max people)', 'Gäste empfangen (max. Personen)', 'Druženje (maks. osoba)')}</div>
													<div className="fw-semibold">{plan.questionnaire.hostingMaxPeople}</div>
												</div>
											)}

											{plan.questionnaire.childrenPets && plan.questionnaire.childrenPets !== 'none' && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Kids/pets priority', 'Priorität Kinder/Haustiere', 'Prioritet djece/ljubimaca')}</div>
													<div className="fw-semibold text-capitalize">{plan.questionnaire.childrenPets}</div>
												</div>
											)}

											{plan.questionnaire.kidsCount != null && plan.questionnaire.kidsCount > 0 && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Kids count', 'Anzahl Kinder', 'Broj djece')}</div>
													<div className="fw-semibold">{plan.questionnaire.kidsCount}</div>
												</div>
											)}

											{plan.questionnaire.kidsAges && plan.questionnaire.kidsAges.trim().length > 0 && (
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Kids ages', 'Alter der Kinder', 'Dob djece')}</div>
													<div className="fw-semibold">{plan.questionnaire.kidsAges}</div>
												</div>
											)}

											{plan.questionnaire.petsDescription && plan.questionnaire.petsDescription.trim().length > 0 && (
												<div className="col-12">
													<div className="text-muted small mb-1">{copy('Pets note', 'Hinweis zu Haustieren', 'Napomena o ljubimcima')}</div>
													<div className="text-break">{plan.questionnaire.petsDescription}</div>
												</div>
											)}

											{plan.questionnaire.budgetRange && (
												<div className="col-12">
													<div className="text-muted small mb-1">{copy('Budget range', 'Budgetspanne', 'Raspon budžeta')}</div>
													<div className="fw-semibold">
														€{plan.questionnaire.budgetRange.min.toLocaleString()} - €{plan.questionnaire.budgetRange.max.toLocaleString()}
													</div>
												</div>
											)}

											{plan.questionnaire.timeline && plan.questionnaire.timeline.trim().length > 0 && (
												<div className="col-12">
													<div className="text-muted small mb-1">{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</div>
													<div className="fw-semibold">{plan.questionnaire.timeline}</div>
												</div>
											)}

											{plan.questionnaire.projectStartDate && (
												<div className="col-12">
													<div className="text-muted small mb-1">{copy('Preferred start date', 'Bevorzugtes Startdatum', 'Željeni datum početka')}</div>
													<div className="fw-semibold">
														{new Date(plan.questionnaire.projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
													</div>
												</div>
											)}
										</div>

										<details className="mt-4">
											<summary style={{ cursor: 'pointer', fontWeight: 700 }}>
												{copy('Smart question responses (answered)', 'Antworten auf intelligente Fragen (beantwortet)', 'Odgovori na pametna pitanja (odgovoreno)')}
											</summary>
											<div className="mt-3">
												{(() => {
													const q = plan.questionnaire!;
													const aiAnswers = (q.aiAnswers ?? {}) as Record<string, any>;
													const aiQuestionComments = (q.aiQuestionComments ?? {}) as Record<string, string>;
													const suggested = q.aiAnalysis?.suggestedQuestions ?? [];

													const answerKeys = Object.keys(aiAnswers).filter((k) => /^ai_q_\d+$/.test(k));
													const validAnswerKeys = answerKeys.filter((k) => {
														const v = aiAnswers[k];
														if (v == null) return false;
														if (typeof v === 'string') return v.trim().length > 0;
														if (Array.isArray(v)) return v.some((x) => String(x ?? '').trim().length > 0);
														return true;
													});

													const indices = new Set<number>();
													for (const k of validAnswerKeys) {
														const idx = Number(k.match(/^ai_q_(\d+)$/)?.[1]);
														if (!Number.isNaN(idx)) indices.add(idx);
													}
													for (const k of Object.keys(aiQuestionComments)) {
														const idx = Number(k);
														if (!Number.isNaN(idx) && aiQuestionComments[k]?.trim().length) indices.add(idx);
													}

													const sorted = Array.from(indices).sort((a, b) => a - b);
													if (sorted.length === 0) {
														return <p className="text-muted mb-0">{copy('No Smart Questions were answered.', 'Keine intelligenten Fragen wurden beantwortet.', 'Nijedno pametno pitanje nije odgovoreno.')}</p>;
													}

													const formatAnswer = (val: any): string => {
														if (val == null) return '';
														if (Array.isArray(val)) return val.filter((x) => String(x ?? '').trim().length > 0).map(String).join(', ');
														if (typeof val === 'boolean') return val ? 'Yes' : 'No';
														const str = String(val);
														if (str === 'yes') return 'Yes';
														if (str === 'no') return 'No';
														return str;
													};

													return (
														<div>
															{sorted.map((idx) => {
																const answerKey = `ai_q_${idx}`;
																const answerVal = aiAnswers[answerKey];
																const commentVal = aiQuestionComments[String(idx)];
																const qText = suggested?.[idx]?.question;
																const title = qText?.trim().length ? qText : copy('Smart question', 'Intelligente Frage', 'Pametno pitanje');
																const answerText = formatAnswer(answerVal);
																const hasAnswer = answerText.trim().length > 0;
																const hasComment = Boolean(commentVal && commentVal.trim().length);

																if (!hasAnswer && !hasComment) return null;

																return (
																	<div key={idx} className="mb-3 pb-3 border-bottom">
																		<div className="fw-semibold mb-1">{title}</div>
																		{hasAnswer && <div className="small text-muted">{copy('Answer:', 'Antwort:', 'Odgovor:')} {answerText}</div>}
																		{hasComment && (
																			<div className="small mt-1">
																				<i className="bi bi-chat-left-text me-1" />
																				<strong>{copy('Your note:', 'Ihre Notiz:', 'Vaša bilješka:')}</strong> <span className="text-break">{commentVal}</span>
																			</div>
																		)}
																	</div>
																);
															})}
														</div>
													);
												})()}
											</div>
										</details>
									</div>
								</details>
							)}
							<div className="row g-3 mt-3">
								<div className="col-12 col-md-6">
									<div className="mb-3 mb-md-0">
										<label className="form-label">{copy('Project start date', 'Projektstartdatum', 'Datum početka projekta')}</label>
										<div className="input-group">
											<span
												className="input-group-text bg-white"
												role="button"
												tabIndex={0}
												onClick={() => {
													const input = projectStartDateInputRef.current;
													if (!input) return;
													input.focus();
													(input as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
												}}
												onKeyDown={(e) => {
													if (e.key === 'Enter' || e.key === ' ') {
														e.preventDefault();
														projectStartDateInputRef.current?.focus();
														(projectStartDateInputRef.current as HTMLInputElement & { showPicker?: () => void })?.showPicker?.();
													}
												}}
											>
												<i className="bi bi-calendar3 text-muted"></i>
											</span>
											<input
												ref={projectStartDateInputRef}
												type="date"
												className="form-control"
												min={new Date().toISOString().split('T')[0]}
												value={projectStartDate}
												onChange={e => {
													setHasTouchedProjectStartDate(true);
													setProjectStartDate(e.target.value);
												}}
											/>
										</div>
										<div className="form-text small">{copy('When you plan to start work on this project.', 'Wann Sie planen, mit diesem Projekt zu beginnen.', 'Kada planirate započeti rad na ovom projektu.')}</div>
										{preferredStartDate && (
											<div className="form-text small">
												{copy('Preferred by seeker:', 'Bevorzugt vom Suchenden:', 'Željeno od vlasnika:')} {new Date(preferredStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
											</div>
										)}
										{isProjectStartBeforePreferred && (
											<div className="alert alert-warning mt-2 py-2 px-3 small mb-0">
												<i className="bi bi-exclamation-triangle me-1"></i>
												{copy('You selected a start date', 'Sie haben ein Startdatum gewählt', 'Odabrali ste datum početka')} <strong>{copy('before', 'vor', 'prije')}</strong> {copy("the seeker's preferred date. This is okay, but you may want to confirm with them.", 'dem vom Suchenden bevorzugten Datum. Das ist in Ordnung, aber Sie sollten es ggf. bestätigen.', 'datuma koji preferira vlasnik. To je u redu, ali možda to želite potvrditi s njim.')}
											</div>
										)}
									</div>
								</div>
								<div className="col-12 col-md-6">
									<div className="mb-3 mb-md-0">
										<label className="form-label">{copy('Offer valid until', 'Angebot gültig bis', 'Ponuda vrijedi do')}</label>
										<div className="input-group">
											<span
												className="input-group-text bg-white"
												role="button"
												tabIndex={0}
												onClick={() => {
													const input = validUntilInputRef.current;
													if (!input) return;
													input.focus();
													(input as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
												}}
												onKeyDown={(e) => {
													if (e.key === 'Enter' || e.key === ' ') {
														e.preventDefault();
														validUntilInputRef.current?.focus();
														(validUntilInputRef.current as HTMLInputElement & { showPicker?: () => void })?.showPicker?.();
													}
												}}
											>
												<i className="bi bi-calendar3 text-muted"></i>
											</span>
											<input
												ref={validUntilInputRef}
												type="date"
												className="form-control"
												value={validUntil}
												onChange={e => {
													setHasTouchedValidUntil(true);
													setValidUntil(e.target.value);
												}}
												aria-describedby="valid-until-help"
											/>
										</div>
										<div id="valid-until-help" className="form-text small">{copy('The seeker can accept this offer until this date.', 'Der Suchende kann dieses Angebot bis zu diesem Datum annehmen.', 'Vlasnik može prihvatiti ovu ponudu do ovog datuma.')}</div>
										{isValidUntilTooEarly && (
											<div className="alert alert-warning mt-2 py-2 px-3 small mb-0">
												<i className="bi bi-exclamation-triangle me-1"></i>
												{copy('This expiry date is quite early. Consider giving the seeker more time to accept before the preferred start date.', 'Dieses Ablaufdatum ist recht früh. Erwägen Sie, dem Suchenden vor dem gewünschten Startdatum mehr Zeit zu geben.', 'Ovaj datum isteka je prilično rano. Razmislite da vlasniku date više vremena za prihvat prije željenog početka.')}
											</div>
										)}
									</div>
								</div>
							</div>
							<div className="mb-4 mt-3">
								<label className="form-label">{copy('Timeline / Production Estimate', 'Zeitplan / Produktionsschätzung', 'Vremenski plan / procjena izvedbe')}</label>
								<input
									type="text"
									className="form-control"
									placeholder={copy('e.g. Mobilize in 2 weeks, delivery in 5 weeks', 'z. B. Start in 2 Wochen, Fertigstellung in 5 Wochen', 'npr. početak za 2 tjedna, završetak za 5 tjedana')}
									value={timelineEstimate}
									onChange={e => setTimelineEstimate(e.target.value)}
								/>
								<div className="form-text small">
									{copy(
										'Tip: keep this concrete (start window + key milestone + expected completion).',
										'Tipp: Formulieren Sie dies konkret (Startfenster + Meilenstein + erwartete Fertigstellung).',
										'Savjet: navedite konkretno (prozor početka + ključna faza + očekivani završetak).'
									)}
								</div>
							</div>
							<div className="mb-3">
								<label className="form-label">{copy('Notes for the seeker (optional)', 'Hinweise für den Suchenden (optional)', 'Bilješke za vlasnika (opcionalno)')}</label>
								<textarea
									className="form-control"
									rows={3}
									placeholder={copy('Summarize what is included in your offer.', 'Fassen Sie zusammen, was in Ihrem Angebot enthalten ist.', 'Sažmite što je uključeno u vašu ponudu.')}
									value={notes}
									onChange={e => setNotes(e.target.value)}
								/>
								<div className="form-text small">
									{copy(
										'Use this space for assumptions, what is included, and any dependencies on seeker decisions.',
										'Nutzen Sie diesen Bereich für Annahmen, enthaltene Leistungen und Abhängigkeiten von Entscheidungen des Suchenden.',
										'Iskoristite ovaj prostor za pretpostavke, što je uključeno i ovisnosti o odlukama vlasnika.'
									)}
								</div>
							</div>
							
							{/* Gardener Selection Section */}
							{availableGardeners.length > 0 && (
								<div className="mb-4">
									<label className="form-label">{copy('Select Gardeners for This Project', 'Gärtner für dieses Projekt auswählen', 'Odaberite vrtlare za ovaj projekt')}</label>
									<div className="card border">
										<div className="card-body">
											{isLoadingGardeners ? (
												<div className="text-center py-3">
													<div className="spinner-border spinner-border-sm text-success" role="status">
														<span className="visually-hidden">{copy('Loading...', 'Wird geladen...', 'Učitavanje...')}</span>
													</div>
													<p className="small text-muted mt-2 mb-0">{copy('Loading gardeners...', 'Gärtner werden geladen...', 'Učitavanje vrtlara...')}</p>
												</div>
											) : (
												<>
													<div className="row g-2 mb-3">
														<div className="col-12 col-md-6">
															<input
																type="search"
																className="form-control form-control-sm"
																placeholder={copy('Search by name or email...', 'Suche nach Name oder E-Mail...', 'Pretraži po imenu ili e-pošti...')}
																value={gardenerSearch}
																onChange={(e) => setGardenerSearch(e.target.value)}
															/>
														</div>
														<div className="col-6 col-md-3">
															<select
																className="form-select form-select-sm"
																value={skillFilter}
																onChange={(e) => setSkillFilter(e.target.value)}
															>
																<option value="all">{copy('All skills', 'Alle Fähigkeiten', 'Sve vještine')}</option>
																{SKILLS_OPTIONS.map((skill) => (
																	<option key={skill} value={skill}>
																		{getSkillLabel(skill, language)}
																	</option>
																))}
															</select>
														</div>
														<div className="col-6 col-md-3">
															<select
																className="form-select form-select-sm"
																value={availabilityFilter}
																onChange={(e) => setAvailabilityFilter(e.target.value as typeof availabilityFilter)}
															>
																<option value="all">{copy('All availability', 'Gesamte Verfügbarkeit', 'Sva dostupnost')}</option>
																<option value="available">{copy('Available only', 'Nur verfügbar', 'Samo dostupni')}</option>
																<option value="has_conflict">{copy('With conflicts only', 'Nur mit Konflikten', 'Samo s konfliktima')}</option>
															</select>
														</div>
													</div>
													<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
														<div className="small text-muted">
															{copy('Gardeners', 'Gärtner', 'Vrtlari')}: {filteredGardeners.length}
														</div>
													</div>
													<div className="mb-3">
														{filteredGardeners.length > 0 ? (
															<div className="row g-2">
															{(showAllGardeners ? filteredGardeners : filteredGardeners.slice(0, 10)).map((gardener) => {
															const isSelected = selectedGardeners.includes(gardener.id);
															const availability = availabilityResults.find(r => Number(r.gardener_id) === Number(gardener.id));
															const hasConflict = availability && !availability.available;
															
															return (
																<div key={gardener.id} className="col-12 col-md-6">
																	<div className={`rounded p-2 h-100 ${isSelected ? 'border border-success-subtle bg-success-subtle' : 'border bg-white'}`}>
																		<div className="d-flex align-items-start gap-2">
																			<input
																				className="form-check-input mt-1 flex-shrink-0"
																				type="checkbox"
																				id={`gardener-${gardener.id}`}
																				checked={isSelected}
																				onChange={(e) => {
																					if (e.target.checked) {
																						setSelectedGardeners([...selectedGardeners, gardener.id]);
																					} else {
																						setSelectedGardeners(selectedGardeners.filter(id => id !== gardener.id));
																					}
																				}}
																			/>
																			<label className="d-block flex-grow-1 mb-0" style={{ minWidth: 0 }} htmlFor={`gardener-${gardener.id}`}>
																				<span className="fw-medium d-block text-break">{gardener.display_name || gardener.name || gardener.email}</span>
																				<span className="small text-muted d-block text-break">
																					{gardenerSkillsMap(gardener.id).length > 0
																						? gardenerSkillsMap(gardener.id).map(s => getSkillLabel(s, language)).join(', ')
																						: copy('No skills set', 'Keine Fähigkeiten gesetzt', 'Nisu postavljene vještine')}
																					{isSelected && (
																						<button
																							type="button"
																							className="btn btn-link btn-sm p-0 ms-1 text-success align-baseline"
																							onClick={(e) => { e.preventDefault(); setEditingSkillsForGardenerId(g => g === gardener.id ? null : gardener.id); }}
																							title={copy('Edit skills for this offer', 'Fähigkeiten für dieses Angebot bearbeiten', 'Uredi vještine za ovu ponudu')}
																						>
																							<i className="bi bi-pencil-square" />
																						</button>
																					)}
																				</span>
																				<div className="d-flex flex-wrap gap-1 mt-1">
																					{hasConflict && (
																						<span className="badge text-bg-warning" title={availability?.conflicts?.map(c => c.project_name).join(', ')}>
																							{copy('Conflict', 'Konflikt', 'Konflikt')}
																						</span>
																					)}
																					{isSelected && availability?.available && (
																						<span className="badge text-bg-success">{copy('Available', 'Verfügbar', 'Dostupno')}</span>
																					)}
																				</div>
																			</label>
																		</div>
																	</div>
																</div>
															);
														})}
															</div>
														) : (
															<p className="text-muted small mb-0">
																{copy('No gardeners match your search and filters.', 'Keine Gärtner entsprechen Ihrer Suche und den Filtern.', 'Nijedan vrtlar ne odgovara pretrazi i filtrima.')}
															</p>
														)}
														{filteredGardeners.length > 10 && (
															<div className="mt-2">
																<button
																	type="button"
																	className="btn btn-sm btn-outline-secondary"
																	onClick={() => setShowAllGardeners((prev) => !prev)}
																>
																	{showAllGardeners
																		? copy('Show first 10', 'Erste 10 anzeigen', 'Prikaži prvih 10')
																		: copy(`Show all (${filteredGardeners.length})`, `Alle anzeigen (${filteredGardeners.length})`, `Prikaži sve (${filteredGardeners.length})`)}
																</button>
															</div>
														)}
													</div>
													{isCheckingAvailability && (
														<div className="small text-muted">
															<i className="bi bi-hourglass-split me-1"></i>
															{copy('Checking availability...', 'Verfügbarkeit wird geprüft...', 'Provjera dostupnosti...')}
														</div>
													)}
													{availabilityResults.some(r => !r.available && r.conflicts && r.conflicts.length > 0) && (
														<div className="alert alert-warning small mb-0">
															<strong>{copy('Availability Conflicts:', 'Verfügbarkeitskonflikte:', 'Konflikti dostupnosti:')}</strong>
															<ul className="mb-0 mt-1">
																{availabilityResults
																	.filter(r => !r.available && r.conflicts && r.conflicts.length > 0)
																	.map((result, idx) => {
																		const gardener = availableGardeners.find(
																			g => Number(g.id) === Number(result.gardener_id)
																		);
																		return (
																			<li key={idx}>
																				{gardener?.display_name || gardener?.name}:{' '}
																				{result.conflicts
																					?.map(c => {
																						const start = new Date(c.conflict_start).toLocaleDateString('en-GB', {
																							day: 'numeric',
																							month: 'short'
																						});
																						const end = new Date(c.conflict_end).toLocaleDateString('en-GB', {
																							day: 'numeric',
																							month: 'short'
																						});
																						return `${c.project_name} – ${start} ${copy('to', 'bis', 'do')} ${end}`;
																					})
																					.join('; ')}
																			</li>
																		);
																	})}
															</ul>
														</div>
													)}
													{/* Skills coverage — always visible so user sees required/missing before selecting gardeners */}
													<div className="mt-3 p-2 border rounded bg-light small">
													<div className="fw-medium mb-1">{copy('Skills coverage', 'Fähigkeitenabdeckung', 'Pokrivenost vještina')}</div>
														{displayedAiContent?.recommended_team_size != null && displayedAiContent.recommended_team_size >= 1 && (
															<div className="mb-1">
																<span className="text-muted">{copy('Recommended team:', 'Empfohlenes Team:', 'Preporučeni tim:')}</span>{' '}
																<span className="fw-medium text-success">
																	{displayedAiContent.recommended_team_size} {displayedAiContent.recommended_team_size === 1 ? copy('person', 'Person', 'osoba') : copy('people', 'Personen', 'osobe')}
																</span>
																<span className="text-muted ms-1">
																	({copy('you selected', 'Sie haben ausgewählt', 'odabrali ste')} {selectedGardeners.length})
																</span>
															</div>
														)}
														{(() => {
															const covered = getSkillsCovered(selectedGardeners, gardenerSkillsMap);
															const missing = getMissingSkills(selectedGardeners, gardenerSkillsMap, skillsNeededForPlan);
															const fromPlan = Array.isArray(displayedAiContent?.required_skills) && displayedAiContent.required_skills.length > 0;
															return (
																<>
																	{fromPlan && (
																		<div className="mb-1">
																			<span className="text-muted">{copy('Required for this plan:', 'Erforderlich für diesen Plan:', 'Potrebno za ovaj plan:')}</span>{' '}
																			{skillsNeededForPlan.map(s => (
																				<span key={s} className="badge bg-secondary me-1">{getSkillLabel(s, language)}</span>
																			))}
																		</div>
																	)}
																	<div className="mb-1">
																		<span className="text-muted">{copy('Skills covered:', 'Abgedeckte Fähigkeiten:', 'Pokrivene vještine:')}</span>{' '}
																		{covered.length > 0 ? (
																			covered.map(s => (
																				<span key={s} className="badge text-bg-success me-1">{getSkillLabel(s, language)}</span>
																			))
																		) : (
																			<span className="text-muted">{copy('—', '—', '—')}</span>
																		)}
																	</div>
																	{missing.length > 0 && (
																		<div>
																			<span className="text-muted">{copy('Missing skills:', 'Fehlende Fähigkeiten:', 'Nedostajuće vještine:')}</span>{' '}
																			{missing.map(s => (
																				<span key={s} className="badge text-bg-warning me-1">{getSkillLabel(s, language)}</span>
																			))}
																		</div>
																	)}
																</>
															);
														})()}
														<div className="mt-2">
															<button
																type="button"
																className="btn btn-sm btn-outline-success"
																onClick={handleAutoAssignButtonClick}
																disabled={timelineTasks.length === 0 || selectedGardeners.length === 0 || isAutoAssigningTimeline}
																title={copy(
																	'Assign selected gardeners to tasks based on required skills, per-task team size, and availability',
																	'Ausgewählte Gärtner Aufgaben zuweisen (basierend auf benötigten Fähigkeiten, Teamgröße je Aufgabe und Verfügbarkeit)',
																	'Dodijeli odabrane vrtlare zadacima prema potrebnim vještinama, veličini tima po zadatku i dostupnosti'
																)}
															>
																{isAutoAssigningTimeline ? (
																	<>
																		<span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
																		{copy('Auto-assigning gardeners...', 'Gärtner werden automatisch zugewiesen...', 'Automatska dodjela vrtlara...')}
																	</>
																) : (
																	<>
																		<i className="bi bi-people-fill me-1" aria-hidden="true"></i>
																		{copy('Auto-assign gardeners', 'Gärtner automatisch zuweisen', 'Automatski dodijeli vrtlare')}
																	</>
																)}
															</button>
															{timelineTasks.length > 0 && selectedGardeners.length > 0 && (
																<div className="form-text mt-1 mb-0">
																	{copy(
																		`${timelineAssignedCount} of ${timelineTasks.length} tasks have gardeners assigned. After you change task dates, run auto-assign again so availability and scheduling stay in sync.`,
																		`${timelineAssignedCount} von ${timelineTasks.length} Aufgaben haben zugewiesene Gärtner. Nach Änderung der Aufgabentermine erneut automatisch zuweisen, damit Verfügbarkeit und Planung stimmen.`,
																		`${timelineAssignedCount} od ${timelineTasks.length} zadataka ima dodijeljene vrtlare. Nakon promjene datuma zadataka ponovno pokrenite automatsku dodjelu radi usklađenosti dostupnosti i rasporeda.`
																	)}
																</div>
															)}
														</div>
															{editingSkillsForGardenerId != null && (() => {
																const g = availableGardeners.find(a => a.id === editingSkillsForGardenerId);
																const currentSkills = gardenerSkillsMap(editingSkillsForGardenerId);
																return (
																	<div className="mt-3 p-2 border rounded bg-white">
																		<div className="fw-medium mb-2">
																			{copy('Edit skills for', 'Fähigkeiten bearbeiten für', 'Uredi vještine za')} {g?.display_name || g?.name || copy('gardener', 'Gärtner', 'vrtlara')} {copy('(this offer only)', '(nur für dieses Angebot)', '(samo za ovu ponudu)')}
																		</div>
																		<div className="d-flex flex-wrap gap-2">
																			{SKILLS_OPTIONS.map((skill) => {
																				const checked = currentSkills.includes(skill);
																				return (
																					<div key={skill} className="form-check form-check-inline mb-0">
																						<input
																							id={`offer-skill-${editingSkillsForGardenerId}-${skill}`}
																							className="form-check-input"
																							type="checkbox"
																							checked={checked}
																							onChange={(e) => {
																								setGardenerSkillsOverrides(prev => {
																									const base = prev[editingSkillsForGardenerId] ?? g?.skills ?? [];
																									const next = e.target.checked
																										? [...base, skill]
																										: base.filter(s => s !== skill);
																									return { ...prev, [editingSkillsForGardenerId]: next };
																								});
																							}}
																						/>
																						<label className="form-check-label small" htmlFor={`offer-skill-${editingSkillsForGardenerId}-${skill}`}>
																							{getSkillLabel(skill, language)}
																						</label>
																					</div>
																				);
																			})}
																		</div>
																		<button
																			type="button"
																			className="btn btn-sm btn-outline-secondary mt-2"
																			onClick={() => setGardenerSkillsOverrides(prev => {
																				const next = { ...prev };
																				delete next[editingSkillsForGardenerId];
																				return next;
																			})}
																		>
																			{copy('Reset to profile skills', 'Auf Profil-Fähigkeiten zurücksetzen', 'Vrati na profilne vještine')}
																		</button>
																		<button
																			type="button"
																			className="btn btn-sm btn-success ms-2 mt-2"
																			onClick={() => setEditingSkillsForGardenerId(null)}
																		>
																			{copy('Done', 'Fertig', 'Gotovo')}
																		</button>
																	</div>
																);
															})()}
														</div>
												</>
											)}
										</div>
									</div>
								</div>
							)}
							
							{/* Timeline & Resources Section */}
							<div className="mb-4">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<label className="form-label mb-0">{copy('Timeline & Resources', 'Zeitplan und Ressourcen', 'Vremenski plan i resursi')}</label>
								</div>
									<div className="card border">
										<div className="card-body">
											<div className="mb-3">
												<label className="form-label small">{copy('Project Start Date', 'Projektstartdatum', 'Datum početka projekta')}</label>
												<div className="form-text small">
													{projectStartDate
														? <>
																{copy('Start:', 'Start:', 'Početak:')} <strong>{new Date(projectStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>. {copy('Controlled above in the main offer details. Adjust it there to update this timeline.', 'Oben in den Hauptangebotsdetails gesteuert. Passen Sie es dort an, um diesen Zeitplan zu aktualisieren.', 'Kontrolirano gore u glavnim detaljima ponude. Prilagodite tamo za ažuriranje ovog vremenskog plana.')}
															</>
														: copy('Controlled above in the main offer details. Adjust it there to update this timeline.', 'Oben in den Hauptangebotsdetails gesteuert. Passen Sie es dort an, um diesen Zeitplan zu aktualisieren.', 'Kontrolirano gore u glavnim detaljima ponude. Prilagodite tamo za ažuriranje ovog vremenskog plana.')}
												</div>
											</div>
											<div>
												{companyCountryCode === 'DE' && seasonalBanConflicts.length > 0 && (
													<div className="alert alert-warning small py-2 mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
														<div>
															<i className="bi bi-exclamation-triangle me-1"></i>
															{copy(
																`${seasonalBanConflicts.length} timeline task(s) are flagged as major woody cutting and currently fall in Germany's restricted period (Mar 1-Sep 30).`,
																`${seasonalBanConflicts.length} Zeitplan-Aufgabe(n) sind als starker Gehölzschnitt markiert und liegen derzeit im deutschen Schutzzeitraum (1. März-30. Sept.).`,
																`${seasonalBanConflicts.length} zadatak(a) je označeno kao veći rez drvenastih biljaka i trenutno pada u njemačko ograničeno razdoblje (1.3.-30.9.).`
															)}
														</div>
														<button
															type="button"
															className="btn btn-sm btn-outline-warning"
															onClick={handleRescheduleSeasonalBanTasks}
															disabled={isReschedulingLegalTasks}
														>
															{isReschedulingLegalTasks
																? copy('Rescheduling...', 'Wird neu geplant...', 'Premještanje...')
																: copy('Move to next legal slot', 'Auf nächsten zulässigen Termin verschieben', 'Premjesti na sljedeći dopušteni termin')}
														</button>
													</div>
												)}
												{timelineTasks.length === 0 && (
													<div className="alert alert-warning small py-2 mb-2">
														<i className="bi bi-exclamation-triangle me-1"></i>
														{copy('Add at least one phase to the timeline so the seeker sees a clear schedule.', 'Fügen Sie dem Zeitplan mindestens eine Phase hinzu, damit der Suchende einen klaren Ablauf sieht.', 'Dodajte barem jednu fazu u vremenski plan kako bi vlasnik vidio jasan raspored.')}
													</div>
												)}
												{timelineReorderNotice && (
													<div className="alert alert-info small py-2 mb-2">
														<i className="bi bi-info-circle me-1"></i>
														{copy(
															'Task order changed — gardener assignments were cleared. Run auto-assign again if needed.',
															'Aufgabenreihenfolge geändert — Gärtnerzuweisungen wurden entfernt. Bei Bedarf erneut automatisch zuweisen.',
															'Redoslijed zadataka je promijenjen — dodjele vrtlara su uklonjene. Po potrebi ponovno pokrenite automatsku dodjelu.'
														)}
													</div>
												)}
												{showTimelineDateEditHint && (
													<div className="alert alert-info small py-2 mb-2">
														<i className="bi bi-info-circle me-1"></i>
														{copy(
															'Task dates were updated. Run auto-assign gardeners again so team availability and scheduling stay in sync.',
															'Aufgabentermine wurden geändert. Weisen Sie die Gärtner erneut automatisch zu, damit Verfügbarkeit und Planung stimmen.',
															'Datumi zadataka su ažurirani. Ponovno pokrenite automatsku dodjelu vrtlara kako bi dostupnost tima i raspored ostali usklađeni.'
														)}
													</div>
												)}
												{timelineTasksInExecutionOrder.length > 0 && (
													<div className="mb-3">
														<h6 className="small fw-semibold mb-2">
															{copy('Tasks & gardeners', 'Aufgaben & Gärtner', 'Zadaci i vrtlari')}
														</h6>
														<p className="small text-muted mb-2">
															{timelineTasksInExecutionOrder.length > 1
																? copy(
																		'Assign gardeners per step or use auto-assign above. Reorder steps before auto-assign if needed. Durations are working days.',
																		'Weisen Sie Gärtner pro Schritt zu oder nutzen Sie die automatische Zuweisung oben. Sortieren Sie bei Bedarf vor der automatischen Zuweisung. Die Dauer gilt in Arbeitstagen.',
																		'Dodijelite vrtlare po koraku ili koristite automatsku dodjelu gore. Po potrebi promijenite redoslijed prije automatske dodjele. Trajanja su radni dani.'
																	)
																: copy(
																		'Assign gardeners for this step or use auto-assign above.',
																		'Weisen Sie Gärtner für diesen Schritt zu oder nutzen Sie die automatische Zuweisung oben.',
																		'Dodijelite vrtlare za ovaj korak ili koristite automatsku dodjelu gore.'
																	)}
														</p>
														<ul className="list-group list-group-flush border rounded">
															{timelineTasksInExecutionOrder.map((task, idx) => {
																const assigneeIds = getTaskAssigneeIds(task);
																const requiredForTask = resolveTaskRequiredGardeners(task, planRecommendedTeamSize);
																return (
																	<li
																		key={task.id}
																		className="list-group-item d-flex flex-wrap align-items-center gap-2 py-2 px-3"
																	>
																		<span className="badge text-bg-secondary">{idx + 1}</span>
																		<div className="flex-grow-1 min-width-0" style={{ minWidth: '120px' }}>
																			<div className="small fw-medium text-truncate">{task.name}</div>
																			<div className="text-muted" style={{ fontSize: '0.75rem' }}>
																				{task.duration} {copy('working days', 'Arbeitstage', 'radna dana')}
																				{requiredForTask > 1 && (
																					<span className="ms-1">
																						· {requiredForTask}{' '}
																						{requiredForTask === 1
																							? copy('person', 'Person', 'osoba')
																							: copy('people', 'Personen', 'osobe')}{' '}
																						{copy('suggested', 'empfohlen', 'preporučeno')}
																					</span>
																				)}
																			</div>
																		</div>
																		<div className="flex-shrink-0">
																			<TaskGardenerPicker
																				assigneeIds={assigneeIds}
																				gardeners={teamGardenersForTaskAssign}
																				suggestedAssignees={requiredForTask}
																				disabled={selectedGardeners.length === 0}
																				onChange={(ids) => handleSetTaskGardeners(task.id, ids)}
																				copy={copy}
																			/>
																		</div>
																		{timelineTasksInExecutionOrder.length > 1 && (
																			<div className="btn-group btn-group-sm flex-shrink-0">
																				<button
																					type="button"
																					className="btn btn-outline-secondary"
																					title={copy('Move earlier', 'Nach oben', 'Pomakni ranije')}
																					disabled={idx === 0}
																					onClick={() => handleMoveTimelineTask(idx, 'up')}
																				>
																					<i className="bi bi-arrow-up" aria-hidden="true"></i>
																				</button>
																				<button
																					type="button"
																					className="btn btn-outline-secondary"
																					title={copy('Move later', 'Nach unten', 'Pomakni kasnije')}
																					disabled={idx === timelineTasksInExecutionOrder.length - 1}
																					onClick={() => handleMoveTimelineTask(idx, 'down')}
																				>
																					<i className="bi bi-arrow-down" aria-hidden="true"></i>
																				</button>
																			</div>
																		)}
																	</li>
																);
															})}
														</ul>
													</div>
												)}
												<div className="d-flex justify-content-between align-items-center mb-2">
													<h6 className="small mb-0">{copy('Detailed Gantt Chart', 'Detailliertes Gantt-Diagramm', 'Detaljni Gantt grafikon')}</h6>
													<div className="small text-muted">
														<i className="bi bi-info-circle me-1"></i>
														{plan?.aiGenerated?.instructions?.length && timelineTasks.some(t => t.id?.startsWith('plan-step-'))
															? copy('Timeline from plan implementation instructions. Click tasks to edit.', 'Zeitplan aus den Umsetzungsanweisungen des Plans. Klicken Sie auf Aufgaben zum Bearbeiten.', 'Vremenski plan iz uputa za provedbu plana. Kliknite zadatke za uređivanje.')
															: copy('Auto-generated timeline based on budget. Click tasks to edit.', 'Automatisch generierter Zeitplan basierend auf dem Budget. Klicken Sie auf Aufgaben zum Bearbeiten.', 'Automatski generiran vremenski plan na temelju budžeta. Kliknite zadatke za uređivanje.')}
													</div>
												</div>
												<div className="small text-muted mb-2">
													<span className="badge text-bg-warning text-dark me-1">!</span>
													{copy(
														'Task may be affected by seasonal legal cutting rules; hover the marker for details.',
														'Aufgabe kann von saisonalen rechtlichen Schnittregeln betroffen sein; fahren Sie fuer Details mit der Maus ueber das Symbol.',
														'Na zadatak mogu utjecati sezonska pravila rezidbe; prijeđite misem preko oznake za detalje.'
													)}
												</div>
												<GanttChart
													tasks={timelineTasks}
													gardeners={availableGardeners.map((g) => ({ id: String(g.id), name: g.display_name || g.name || '', skills: g.skills }))}
													editable={true}
													companyCountryCode={companyCountryCode}
													companyCalendar={companyCalendar}
													compactLegalBadge={true}
													className="hide-status-legend"
													onTaskUpdate={(taskId, updates) => {
														if (!projectStartDate) return;
														const updated = applyWorkdayTaskUpdate(
															timelineTasks,
															taskId,
															updates,
															new Date(projectStartDate),
															companyCalendar
														);
														if (
															updates.startDate != null ||
															updates.endDate != null ||
															updates.duration != null
														) {
															setShowTimelineDateEditHint(true);
														}
														commitTimelineTasks(updated, { syncPlan: true, markManual: true });
													}}
												/>
											</div>
										</div>
									</div>
							</div>
						</div>
					</div>
					{/* Full Plan Details */}
					{plan.aiGenerated && plan.questionnaire ? (
						<>
							{/* Change something? + revision history */}
							{plan.databaseId && (plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl) && (
								<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-4 mb-2">
									<button
										type="button"
										className="btn btn-outline-success btn-sm"
										onClick={() => setEditModalOpen(true)}
										disabled={isRegenerating}
										title={copy('Describe changes to regenerate the plan', 'Beschreiben Sie Änderungen, um den Plan neu zu generieren', 'Opišite promjene za ponovno generiranje plana')}
									>
										<i className="bi bi-pencil-square me-1"></i>{copy('Change something?', 'Etwas ändern?', 'Promijeniti nešto?')}
									</button>
								</div>
							)}
							{hasOfferSpecificEdits && (
								<div className="alert alert-success d-flex align-items-center gap-2 mb-3">
									<i className="bi bi-check-circle-fill"></i>
									<div>
										<strong>{copy('Done editing', 'Bearbeitung beendet', 'Završeno uređivanje')}</strong>
									</div>
								</div>
							)}
							{viewingVersionKey !== 'current' && (
								<div className="alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
									<span>{copy('Viewing a previous version.', 'Sie sehen eine frühere Version.', 'Prikazuje se prethodna verzija.')}</span>
									<div className="d-flex gap-2">
										<button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setViewingVersionKey('current')}>
											{copy('View current', 'Aktuelle Version', 'Prikaži trenutnu')}
										</button>
										<button type="button" className="btn btn-sm btn-success" onClick={() => handleUseVersionForOffer(viewingVersionKey)}>
											{copy('Use this version for my offer', 'Diese Version für mein Angebot verwenden', 'Koristi ovu verziju za moju ponudu')}
										</button>
									</div>
								</div>
							)}
							{/* Before/After Comparison */}
							<div className="card mt-4">
								<div className="card-header">
									<h5 className="mb-0">{copy('Before & After', 'Vorher & Nachher', 'Prije i poslije')}</h5>
								</div>
								<div className="card-body">
									<div className="row g-3">
										<div className="col-12 col-md-6">
											<div className="text-muted small mb-2">{copy('Current State', 'Aktueller Zustand', 'Trenutno stanje')}</div>
											{plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl ? (
												<>
													<div
														style={{
															width: '100%',
															height: '400px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															backgroundColor: '#f8f9fa',
															borderRadius: '0.375rem',
															cursor: 'pointer',
															overflow: 'hidden',
															border: '1px solid #dee2e6'
														}}
														onClick={() => {
															const afterImageUrl = displayedAiContent?.photoAfterUrl;
															const isPlaceholder = afterImageUrl?.includes('unsplash.com');
															const hasAfterImage = afterImageUrl && !isPlaceholder;
															setImageModalIndex(0);
															setImageModalOpen(true);
														}}
													>
														<img
															src={getImageUrl(plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl)}
															alt="Before"
															className="img-fluid rounded"
															style={{
																maxWidth: '100%',
																maxHeight: '100%',
																objectFit: 'contain',
																pointerEvents: 'none'
															}}
															onError={(e) => {
																console.error('Failed to load before image');
																e.currentTarget.style.display = 'none';
															}}
														/>
													</div>
													<small className="text-muted d-block mt-2">
														<i className="bi bi-zoom-in me-1"></i>{copy('Click to expand', 'Zum Vergrößern klicken', 'Klikni za proširenje')}
													</small>
												</>
											) : (
												<div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
													<span className="text-muted">{copy('No photo provided', 'Kein Foto vorhanden', 'Nema fotografije')}</span>
												</div>
											)}
										</div>
										<div className="col-12 col-md-6">
											<div className="text-muted small mb-2">{copy('Design Vision', 'Designvision', 'Vizija dizajna')}</div>
											{(() => {
												// Check if photoAfterUrl is a placeholder (Unsplash) or a real AI-generated image
												const afterImageUrl = displayedAiContent?.photoAfterUrl;
												const isPlaceholder = afterImageUrl?.includes('unsplash.com');
												
												// Use AI-generated image only if it's not a placeholder
												// Otherwise, fall back to before image
												const beforeImageUrl = plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl;
												const imageToUse = (afterImageUrl && !isPlaceholder) ? afterImageUrl : beforeImageUrl;
												
												if (imageToUse) {
													return (
														<>
															<div
																style={{
																	width: '100%',
																	height: '400px',
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'center',
																	backgroundColor: '#f8f9fa',
																	borderRadius: '0.375rem',
																	cursor: 'pointer',
																	overflow: 'hidden',
																	border: '2px solid #198754'
																}}
																onClick={() => {
																	const hasAfterImage = afterImageUrl && !isPlaceholder;
																	setImageModalIndex(hasAfterImage ? 1 : 0);
																	setImageModalOpen(true);
																}}
															>
																<img
																	src={getImageUrl(imageToUse)}
																	alt={(afterImageUrl && !isPlaceholder) ? copy('After - Design vision', 'Nachher - Designvision', 'Poslije - Dizajnerska vizija') : copy('After - Using before image', 'Nachher - Vorherbild', 'Poslije - Početna fotografija')}
																	className="img-fluid rounded"
																	style={{
																		maxWidth: '100%',
																		maxHeight: '100%',
																		objectFit: 'contain',
																		pointerEvents: 'none'
																	}}
																	onError={(e) => {
																		console.error('Failed to load after image');
																		e.currentTarget.style.display = 'none';
																	}}
																/>
															</div>
															<small className="text-muted d-block mt-2">
																<i className="bi bi-zoom-in me-1"></i>{copy('Click to expand', 'Zum Vergrößern klicken', 'Klikni za proširenje')}
															</small>
														</>
													);
												}
												
												return (
													<div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
														<span className="text-muted">{copy('No photo available', 'Kein Foto verfügbar', 'Fotografija nije dostupna')}</span>
													</div>
												);
											})()}
										</div>
									</div>
								</div>
							</div>

							{/* Version history: chronological, including offer-only edits */}
							{(plan.revisions && plan.revisions.length > 0) || offerRevisions.length > 0 ? (
								<div className="card mt-3">
									<div className="card-body py-2">
										<div className="text-muted small mb-2">{copy('Versions', 'Versionen', 'Verzije')}</div>
										<div className="d-flex flex-wrap gap-2 align-items-center">
											<button
												type="button"
												className="btn btn-sm btn-outline-secondary"
												style={{ width: 120, cursor: 'pointer', borderWidth: viewingVersionKey === 'current' ? 2 : 1, borderColor: viewingVersionKey === 'current' ? '#198754' : '#dee2e6' }}
												onClick={() => setViewingVersionKey('current')}
											>
												{copy('Current offer', 'Aktuelles Angebot', 'Trenutna ponuda')}
											</button>
											{(plan.revisions ?? []).map((_rev, actualIndex) => {
												const versionKey = `plan-${actualIndex}`;
												const isViewing = viewingVersionKey === versionKey;
												return (
													<div key={versionKey} className="d-inline-flex align-items-center gap-1">
														<button
															type="button"
															className="btn btn-sm btn-outline-secondary"
															style={{ width: 100, cursor: 'pointer', borderWidth: isViewing ? 2 : 1, borderColor: isViewing ? '#198754' : '#dee2e6' }}
															onClick={() => setViewingVersionKey(versionKey)}
														>
															v{actualIndex + 1}
														</button>
														<button type="button" className="btn btn-sm btn-outline-success p-1" style={{ fontSize: '0.7rem' }} onClick={() => handleUseVersionForOffer(versionKey)} disabled={isRestoring} title={copy('Use this version for offer', 'Diese Version für das Angebot verwenden', 'Koristi ovu verziju za ponudu')}>
															<i className="bi bi-arrow-counterclockwise"></i>
														</button>
													</div>
												);
											})}
											{offerRevisions.map((_rev, offerIndex) => {
												const versionKey = `offer-${offerIndex}`;
												const isViewing = viewingVersionKey === versionKey;
												return (
													<div key={versionKey} className="d-inline-flex align-items-center gap-1">
														<button
															type="button"
															className="btn btn-sm btn-outline-secondary"
															style={{ width: 120, cursor: 'pointer', borderWidth: isViewing ? 2 : 1, borderColor: isViewing ? '#198754' : '#dee2e6' }}
															onClick={() => setViewingVersionKey(versionKey)}
														>
															{copy('Edit', 'Bearbeitung', 'Uredi')} v{offerIndex + 1}
														</button>
														<button type="button" className="btn btn-sm btn-outline-success p-1" style={{ fontSize: '0.7rem' }} onClick={() => handleUseVersionForOffer(versionKey)} disabled={isRestoring} title={copy('Use this version for offer', 'Diese Version für das Angebot verwenden', 'Koristi ovu verziju za ponudu')}>
															<i className="bi bi-arrow-counterclockwise"></i>
														</button>
													</div>
												);
											})}
										</div>
									</div>
								</div>
							) : null}

							{/* Edit plan: applies to overview, key features, pricing, instructions — above all plan content */}
							<div className="d-flex justify-content-end align-items-center mt-3 mb-1">
								<button
									type="button"
									className="btn btn-success px-4 py-2 rounded-2 d-inline-flex align-items-center gap-2 shadow-sm"
									onClick={() => {
										setIsEditingPlanContent(prev => {
									    return !prev;
										});
									}}
								>
									{isEditingPlanContent ? (
										<><i className="bi bi-check-lg" aria-hidden /><span>{copy('Done editing', 'Bearbeitung beendet', 'Završeno uređivanje')}</span></>
									) : (
										<><i className="bi bi-pencil-square" aria-hidden /><span>{copy('Edit plan', 'Plan bearbeiten', 'Uredi plan')}</span></>
									)}
								</button>
							</div>

							<div className="row g-4 mt-0">
								{/* Plan Details */}
								<div className="col-12 col-lg-8">
									<div className="card mt-4">
										<div className="card-header">
											<h5 className="mb-0">{copy('Plan Overview', 'Planübersicht', 'Pregled plana')}</h5>
										</div>
										<div className="card-body">
											<div className="mb-3">
												<div className="text-muted small">{copy('Style', 'Stil', 'Stil')}</div>
												<div className="fw-bold">{getLocalizedStyleLabel(plan.styleKey, stylePresetsByKey[plan.styleKey]?.title, copy)}</div>
											</div>
											<div className="mb-3">
												<div className="text-muted small">{copy('Garden Size', 'Gartengröße', 'Veličina vrta')}</div>
												<div className="fw-bold">{plan.questionnaire.gardenSize} m²</div>
											</div>
											<div className="mb-3">
												<div className="text-muted small">{copy('Location', 'Standort', 'Lokacija')}</div>
												<div className="fw-bold">{plan.city}</div>
											</div>
											<hr />
											<div className="mb-3">
												<div className="text-muted small mb-2">{copy('Description', 'Beschreibung', 'Opis')}</div>
												{isEditingPlanContent ? (
													<textarea
														className="form-control"
														rows={4}
														value={displayedAiContent!.planDescription}
														onChange={e => setOfferPlanContent(prev => ({ ...(prev ?? JSON.parse(JSON.stringify(displayedAiContent!))), planDescription: e.target.value }))}
													/>
												) : (
													<div className="text-body" style={{ whiteSpace: 'pre-wrap' }}>{displayedAiContent!.planDescription}</div>
												)}
											</div>
										</div>
									</div>

									{/* Key Features */}
									<div className="card mt-4">
										<div className="card-header d-flex justify-content-between align-items-center">
											<h5 className="mb-0">{copy('Key Features', 'Hauptmerkmale', 'Ključne značajke')}</h5>
											{isEditingPlanContent && (
												<button
													type="button"
													className="btn btn-sm btn-outline-success"
													onClick={() => setOfferPlanContent(prev => {
														const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
														return { ...base, keyFeatures: [...base.keyFeatures, ''] };
													})}
												>
													+ {copy('Add', 'Hinzufügen', 'Dodaj')}
												</button>
											)}
										</div>
										<div className="card-body">
											{isEditingPlanContent ? (
												<ul className="list-unstyled mb-0">
													{displayedAiContent!.keyFeatures.map((feature, idx) => (
														<li key={idx} className="mb-3 d-flex align-items-start gap-2">
															<input
																type="text"
																className="form-control form-control-sm flex-grow-1"
																value={feature}
																onChange={e => setOfferPlanContent(prev => {
																	const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
																	const next = [...base.keyFeatures];
																	next[idx] = e.target.value;
																	return { ...base, keyFeatures: next };
																})}
															/>
															<button
																type="button"
																className="btn btn-sm btn-outline-danger p-1"
																title="Remove"
																onClick={() => setOfferPlanContent(prev => {
																	const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
																	return { ...base, keyFeatures: base.keyFeatures.filter((_f: string, i: number) => i !== idx) };
																})}
															>
																<i className="bi bi-trash text-danger"></i>
															</button>
														</li>
													))}
												</ul>
											) : (
												<ul className="list-unstyled mb-0">
													{displayedAiContent!.keyFeatures.map((feature, idx) => (
														<li key={idx} className="mb-2 d-flex align-items-start gap-2">
															<i className="bi bi-check2 text-success mt-1" style={{ fontSize: '1rem' }} aria-hidden />
															<span>{feature}</span>
														</li>
													))}
												</ul>
											)}
										</div>
									</div>

									{/* Instructions */}
									<div className="card mt-4">
										<div className="card-header d-flex justify-content-between align-items-center">
											<h5 className="mb-0">{copy('Implementation Instructions', 'Umsetzungsschritte', 'Upute za izvedbu')}</h5>
											{isEditingPlanContent && (
												<button
													type="button"
													className="btn btn-sm btn-outline-success"
													onClick={() => setOfferPlanContent(prev => {
														const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
														return { ...base, instructions: [...base.instructions, ''] };
													})}
												>
													+ {copy('Add', 'Hinzufügen', 'Dodaj')}
												</button>
											)}
										</div>
										<div className="card-body">
											{isEditingPlanContent ? (
												<ol className="mb-0">
													{displayedAiContent!.instructions.map((instruction, idx) => (
														<li key={idx} className="mb-3 d-flex align-items-start gap-2" style={{ lineHeight: '1.8' }}>
															<span className="me-1">{idx + 1}.</span>
															<input
																type="text"
																className="form-control form-control-sm flex-grow-1"
																value={instruction}
																onChange={e => setOfferPlanContent(prev => {
																	const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
																	const next = [...base.instructions];
																	next[idx] = e.target.value;
																	return { ...base, instructions: next };
																})}
															/>
															<button
																type="button"
																className="btn btn-sm btn-outline-danger p-1"
																title="Remove"
																onClick={() => setOfferPlanContent(prev => {
																	const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
																	return { ...base, instructions: base.instructions.filter((_: string, i: number) => i !== idx) };
																})}
															>
																<i className="bi bi-trash text-danger"></i>
															</button>
														</li>
													))}
												</ol>
											) : (
												<ol className="mb-0 ps-3" style={{ lineHeight: '1.8' }}>
													{displayedAiContent!.instructions.map((instruction, idx) => (
														<li key={idx} className="mb-2">{formatInstruction(instruction)}</li>
													))}
												</ol>
											)}
										</div>
									</div>
								</div>

								{/* Pricing & Tools Sidebar */}
								<div className="col-12 col-lg-4">
									{/* Estimated Pricing: single editable price (min = materials + labor) */}
									<div className="card mt-4">
										<div className="card-header">
											<h5 className="mb-0">{copy('Estimated Pricing', 'Geschätzte Kosten', 'Procijenjena cijena')}</h5>
										</div>
										<div className="card-body">
											<div className="mb-3">
												{isEditingPlanContent ? (
													<>
														<label className="form-label small text-muted mb-1">{copy('Estimated price (€)', 'Geschätzter Preis (€)', 'Procijenjena cijena (€)')}</label>
														<div className="d-flex flex-wrap gap-2 align-items-center">
															<input
																type="number"
																className="form-control fw-semibold"
															value={estimatedPrice}
																onChange={e => {
																	const v = Number(e.target.value);
																if (!Number.isNaN(v)) setEstimatedPrice(v);
																}}
															/>
															<button
																type="button"
															className="btn btn-sm btn-outline-secondary"
															style={{ minWidth: '140px' }}
																onClick={() => setEstimatedPrice(materialsPlusLaborTotal)}
																title={copy('Set to cost (no margin)', 'Auf Kosten setzen (ohne Marge)', 'Postavi na trošak (bez marže)')}
															>
																{copy('Set to minimum', 'Auf Minimum setzen', 'Postavi na minimum')}
															</button>
														</div>
														<div className="small text-muted mt-1">
															{copy('Minimum (materials + labor):', 'Minimum (Material + Arbeit):', 'Minimum (materijali + rad):')} {materialsPlusLaborTotal.toLocaleString()} EUR
														</div>
													</>
												) : (
													<>
														<div className="d-flex justify-content-between align-items-baseline">
															<span className="text-muted">{copy('Estimated price', 'Geschätzter Preis', 'Procijenjena cijena')}</span>
															<span className="fw-bold fs-5 text-success">{estimatedPrice.toLocaleString()} EUR</span>
														</div>
														<div className="small text-muted mt-1">
															{copy('Minimum (materials + labor):', 'Minimum (Material + Arbeit):', 'Minimum (materijali + rad):')} {materialsPlusLaborTotal.toLocaleString()} EUR
														</div>
													</>
												)}
											</div>
											<hr />

											{/* Materials */}
											<div className="small mb-0">
												<div className="d-flex justify-content-between align-items-center mb-2">
													<span className="text-muted">{copy('Materials', 'Materialien', 'Materijali')}</span>
													{isEditingPlanContent && (
														<button
															type="button"
															className="btn btn-sm btn-outline-success py-0"
															onClick={() => setOfferPlanContent(prev => {
																const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
																return { ...base, pricingBreakdown: { ...base.pricingBreakdown, materials: [...base.pricingBreakdown.materials, { name: '', quantity: 0, unit: 'pcs', unitPrice: 0, total: 0 }] } };
															})}
														>
															+ {copy('Add', 'Hinzufügen', 'Dodaj')}
														</button>
													)}
												</div>
												{displayedAiContent!.pricingBreakdown.materials.length === 0 ? (
													<p className="text-muted mb-0 small">{copy('No materials listed.', 'Keine Materialien aufgeführt.', 'Nema navedenih materijala.')}</p>
												) : isEditingPlanContent ? (
													displayedAiContent!.pricingBreakdown.materials.map((m, idx) => {
														const availability = getMockMaterialAvailability(m.name, m.quantity, m.unit);
														return (
															<div key={idx} className="mb-2 p-2 border rounded">
																<div className="d-flex justify-content-between align-items-center mb-1">
																	<input type="text" className="form-control form-control-sm flex-grow-1 me-1" placeholder={copy('Material', 'Material', 'Materijal')} value={m.name} onChange={e => setOfferPlanContent(prev => {
																		const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
																		const next = [...base.pricingBreakdown.materials];
																		next[idx] = { ...next[idx], name: e.target.value, total: next[idx].quantity * next[idx].unitPrice };
																		return { ...base, pricingBreakdown: { ...base.pricingBreakdown, materials: next } };
																	})} />
																	<button type="button" className="btn btn-sm btn-outline-danger p-1" title="Remove" onClick={() => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); return { ...base, pricingBreakdown: { ...base.pricingBreakdown, materials: base.pricingBreakdown.materials.filter((_: unknown, i: number) => i !== idx) } }; })}><i className="bi bi-trash text-danger"></i></button>
																</div>
																<div className="d-flex gap-1 flex-wrap align-items-center">
																	<input type="number" className="form-control form-control-sm" style={{ width: 60 }} placeholder={copy('Qty', 'Menge', 'Kol.')} value={m.quantity || ''} onChange={e => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); const next = [...base.pricingBreakdown.materials]; const q = Number(e.target.value) || 0; next[idx] = { ...next[idx], quantity: q, total: q * next[idx].unitPrice }; return { ...base, pricingBreakdown: { ...base.pricingBreakdown, materials: next } }; })} />
																	<input type="text" className="form-control form-control-sm" style={{ width: 50 }} placeholder={copy('Unit', 'Einheit', 'Jedinica')} value={m.unit} onChange={e => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); const next = [...base.pricingBreakdown.materials]; next[idx] = { ...next[idx], unit: e.target.value }; return { ...base, pricingBreakdown: { ...base.pricingBreakdown, materials: next } }; })} />
																	<input type="number" className="form-control form-control-sm" style={{ width: 70 }} placeholder={copy('Price', 'Preis', 'Cijena')} value={m.unitPrice || ''} onChange={e => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); const next = [...base.pricingBreakdown.materials]; const p = Number(e.target.value) || 0; next[idx] = { ...next[idx], unitPrice: p, total: next[idx].quantity * p }; return { ...base, pricingBreakdown: { ...base.pricingBreakdown, materials: next } }; })} />
																	<span className="small align-self-center">{m.total.toLocaleString()} EUR</span>
																</div>
																<div className="d-flex flex-wrap gap-1 mt-1 small">
																	{availability.status === 'in_stock' && <span className="badge text-bg-success">{copy('In stock', 'Auf Lager', 'Na zalihi')}</span>}
																	{availability.status === 'short' && availability.shortBy && <span className="badge text-bg-warning">{copy('Short by', 'Fehlmenge', 'Nedostaje')} {availability.shortBy.quantity} {availability.shortBy.unit}</span>}
																	{availability.status === 'out_of_stock' && <span className="badge text-bg-danger">{copy('Out of stock', 'Nicht auf Lager', 'Nema na zalihi')}</span>}
																	{availability.leadTime && <span className="badge bg-secondary bg-opacity-75">{formatLocalizedLeadTime(availability.leadTime, copy)}</span>}
																</div>
															</div>
														);
													})
												) : (
													<div className="mb-0">
														{displayedAiContent!.pricingBreakdown.materials.map((m, idx) => {
															const availability = getMockMaterialAvailability(m.name, m.quantity, m.unit);
															const localizedLeadTime = formatLocalizedLeadTime(availability.leadTime, copy);
															const invLabel = availability.status === 'in_stock'
																? (localizedLeadTime ? `${copy('In stock', 'Auf Lager', 'Na zalihi')} · ${localizedLeadTime}` : copy('In stock', 'Auf Lager', 'Na zalihi'))
																: availability.status === 'short' && availability.shortBy
																	? (localizedLeadTime ? `${copy('Short by', 'Fehlmenge', 'Nedostaje')} ${availability.shortBy.quantity} ${availability.shortBy.unit} · ${localizedLeadTime}` : `${copy('Short by', 'Fehlmenge', 'Nedostaje')} ${availability.shortBy.quantity} ${availability.shortBy.unit}`)
																	: availability.status === 'out_of_stock'
																		? (localizedLeadTime ? `${copy('Out of stock', 'Nicht auf Lager', 'Nema na zalihi')} · ${localizedLeadTime}` : copy('Out of stock', 'Nicht auf Lager', 'Nema na zalihi'))
																		: localizedLeadTime || null;
															const invStyle = availability.status === 'in_stock'
																? { backgroundColor: 'rgba(25, 135, 84, 0.1)', borderColor: 'rgba(25, 135, 84, 0.4)', color: '#198754' }
																: availability.status === 'short'
																	? { backgroundColor: 'rgba(255, 193, 7, 0.15)', borderColor: 'rgba(255, 193, 7, 0.5)', color: '#856404' }
																	: availability.status === 'out_of_stock'
																		? { backgroundColor: 'rgba(220, 53, 69, 0.1)', borderColor: 'rgba(220, 53, 69, 0.4)', color: '#dc3545' }
																		: { backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: 'rgba(108, 117, 125, 0.35)', color: '#6c757d' };
															return (
																<div key={idx} className="d-flex justify-content-between align-items-start mb-3">
																	<div className="me-2">
																		<div className="fw-bold">{m.name}</div>
																		<div className="small text-muted">{m.quantity} {m.unit} × {m.unitPrice?.toLocaleString()} EUR/{m.unit}</div>
																		{invLabel && (
																			<span
																				className="small mt-1 d-inline-block px-2 py-1 rounded border fw-medium"
																				style={{ fontSize: '0.75rem', ...invStyle }}
																			>
																				{invLabel}
																			</span>
																		)}
																	</div>
																	<div className="fw-bold text-end text-nowrap">{m.total.toLocaleString()} EUR</div>
																</div>
															);
														})}
													</div>
												)}
											</div>

											<hr className="my-3" />

											{/* Labor */}
											<div className="small mb-3">
												<div className="d-flex justify-content-between align-items-center mb-2">
													<span className="text-muted">{copy('Labor', 'Arbeit', 'Rad')}</span>
													{isEditingPlanContent && (
														<button
															type="button"
															className="btn btn-sm btn-outline-success py-0"
															onClick={() => setOfferPlanContent(prev => {
																const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
																return ({ ...base, pricingBreakdown: { ...base.pricingBreakdown, labor: [...base.pricingBreakdown.labor, { name: '', hours: 0, ratePerHour: 0, total: 0 }] } });
															})}
														>
															+ {copy('Add', 'Hinzufügen', 'Dodaj')}
														</button>
													)}
												</div>
												{displayedAiContent!.pricingBreakdown.labor.length === 0 ? (
													<p className="text-muted mb-0 small">{copy('No labor items.', 'Keine Arbeitspositionen.', 'Nema stavki rada.')}</p>
												) : isEditingPlanContent ? (
													displayedAiContent!.pricingBreakdown.labor.map((l, idx) => (
														<div key={idx} className="mb-2 p-2 border rounded d-flex justify-content-between align-items-start gap-1">
															<div className="flex-grow-1">
																<input type="text" className="form-control form-control-sm mb-1" placeholder={copy('Task', 'Aufgabe', 'Zadatak')} value={l.name} onChange={e => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); const next = [...base.pricingBreakdown.labor]; next[idx] = { ...next[idx], name: e.target.value }; return { ...base, pricingBreakdown: { ...base.pricingBreakdown, labor: next } }; })} />
																<div className="d-flex gap-1">
																	<input type="number" className="form-control form-control-sm" style={{ width: 60 }} placeholder={copy('Hrs', 'Std.', 'Sati')} value={l.hours || ''} onChange={e => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); const next = [...base.pricingBreakdown.labor]; const h = Number(e.target.value) || 0; next[idx] = { ...next[idx], hours: h, total: h * next[idx].ratePerHour }; return { ...base, pricingBreakdown: { ...base.pricingBreakdown, labor: next } }; })} />
																	<input type="number" className="form-control form-control-sm" style={{ width: 70 }} placeholder={copy('Rate', 'Satz', 'Stopa')} value={l.ratePerHour || ''} onChange={e => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); const next = [...base.pricingBreakdown.labor]; const r = Number(e.target.value) || 0; next[idx] = { ...next[idx], ratePerHour: r, total: next[idx].hours * r }; return { ...base, pricingBreakdown: { ...base.pricingBreakdown, labor: next } }; })} />
																	<span className="small align-self-center">{l.total.toLocaleString()} EUR</span>
																</div>
															</div>
															<button type="button" className="btn btn-sm btn-outline-danger p-1" title="Remove" onClick={() => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); return { ...base, pricingBreakdown: { ...base.pricingBreakdown, labor: base.pricingBreakdown.labor.filter((_: unknown, i: number) => i !== idx) } }; })}><i className="bi bi-trash text-danger"></i></button>
														</div>
													))
												) : (
													<div className="mb-0">
														{displayedAiContent!.pricingBreakdown.labor.map((l, idx) => (
															<div key={idx} className="d-flex justify-content-between align-items-center mb-2">
																<span>{l.name}</span>
																<span className="fw-bold text-nowrap">{l.total.toLocaleString()} EUR</span>
															</div>
														))}
													</div>
												)}
											</div>

											{/* Adjust all pricing by X% (edit mode only) */}
											{isEditingPlanContent && (
												<div className="mb-0 p-2 border rounded bg-light small">
																<div className="fw-medium mb-1">{copy('Adjust all pricing', 'Alle Preise anpassen', 'Prilagodi sve cijene')}</div>
													<div className="d-flex flex-wrap align-items-center gap-2">
														{[5, 10, -5].map(pct => (
															<button
																key={pct}
																type="button"
																className="btn btn-sm btn-outline-secondary"
																onClick={() => {
																	const factor = 1 + pct / 100;
																	const base = offerPlanContent ?? JSON.parse(JSON.stringify(displayedAiContent!));
																	const materials = base.pricingBreakdown.materials.map((m: { name: string; quantity: number; unit: string; unitPrice: number; total: number }) => {
																		const newUnitPrice = Math.round(m.unitPrice * factor);
																		return { ...m, unitPrice: newUnitPrice, total: m.quantity * newUnitPrice };
																	});
																	const labor = base.pricingBreakdown.labor.map((l: { name: string; hours: number; ratePerHour: number; total: number }) => {
																		const newRate = Math.round(l.ratePerHour * factor);
																		return { ...l, ratePerHour: newRate, total: l.hours * newRate };
																	});
																	const scaled = normalizePricingBreakdown({
																		...base.pricingBreakdown,
																		materials,
																		labor
																	});
																	setOfferPlanContent({
																		...base,
																		pricingBreakdown: scaled
																	});
																	setEstimatedPrice(scaled.mid);
																}}
															>
																{pct > 0 ? `+${pct}%` : `${pct}%`}
															</button>
														))}
														<span className="text-muted">{copy('Apply to materials, labor and estimated price.', 'Auf Materialien, Arbeit und geschätzten Preis anwenden.', 'Primijeni na materijale, rad i procijenjenu cijenu.')}</span>
													</div>
												</div>
											)}
										</div>
									</div>

									{/* Required Tools */}
									<div className="card mt-4">
										<div className="card-header d-flex justify-content-between align-items-center">
											<h5 className="mb-0"><i className="bi bi-tools me-2"></i>{copy('Required Tools', 'Erforderliche Werkzeuge', 'Potrebni alati')}</h5>
											{isEditingPlanContent && (
												<button
													type="button"
													className="btn btn-sm btn-outline-success"
													onClick={() => setOfferPlanContent(prev => {
														const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!));
														const tools = base.tools ?? [];
														const isObj = tools.length > 0 && typeof (tools as any)[0] === 'object';
														return { ...base, tools: isObj ? [...(tools as Array<{ name: string; description: string }>), { name: '', description: '' }] : [...(tools as string[]), ''] };
													})}
												>
													+ {copy('Add', 'Hinzufügen', 'Dodaj')}
												</button>
											)}
										</div>
										<div className="card-body">
											{displayedAiContent!.tools && displayedAiContent!.tools.length > 0 ? (
												isEditingPlanContent ? (
													typeof displayedAiContent!.tools[0] === 'string' ? (
														<ul className="list-unstyled mb-0 small">
															{(displayedAiContent!.tools as string[]).map((tool, idx) => (
																<li key={idx} className="mb-2 d-flex gap-2 align-items-center">
																	<input type="text" className="form-control form-control-sm flex-grow-1" value={tool} onChange={e => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); const next = [...(base.tools as string[])]; next[idx] = e.target.value; return { ...base, tools: next }; })} />
																	<button type="button" className="btn btn-sm btn-outline-danger p-1" title="Remove" onClick={() => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); return { ...base, tools: (base.tools as string[]).filter((_t: string, i: number) => i !== idx) }; })}><i className="bi bi-trash text-danger"></i></button>
																</li>
															))}
														</ul>
													) : (
														<div className="small">
															{(displayedAiContent!.tools as Array<{ name: string; description: string }>).map((tool, idx) => (
																<div key={idx} className="mb-3 pb-3 border-bottom">
																	<div className="d-flex justify-content-between align-items-center mb-1">
																		<input type="text" className="form-control form-control-sm" placeholder={copy('Tool name', 'Werkzeugname', 'Naziv alata')} value={tool.name} onChange={e => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); const next = [...(base.tools as Array<{ name: string; description: string }>)]; next[idx] = { ...next[idx], name: e.target.value }; return { ...base, tools: next }; })} />
																		<button type="button" className="btn btn-sm btn-outline-danger p-1 ms-1" title="Remove" onClick={() => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); return { ...base, tools: (base.tools as Array<{ name: string; description: string }>).filter((_: { name: string; description: string }, i: number) => i !== idx) }; })}><i className="bi bi-trash text-danger"></i></button>
																	</div>
																	<input type="text" className="form-control form-control-sm" placeholder={copy('Description', 'Beschreibung', 'Opis')} value={tool.description} onChange={e => setOfferPlanContent(prev => { const base = prev ?? JSON.parse(JSON.stringify(displayedAiContent!)); const next = [...(base.tools as Array<{ name: string; description: string }>)]; next[idx] = { ...next[idx], description: e.target.value }; return { ...base, tools: next }; })} />
																</div>
															))}
														</div>
													)
												) : (
													<ul className="list-unstyled mb-0 small">
														{typeof displayedAiContent!.tools[0] === 'string'
															? (displayedAiContent!.tools as string[]).map((tool, idx) => (
																<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																	<i className="bi bi-wrench text-muted mt-1" aria-hidden />
																	<span>{tool}</span>
																</li>
															))
															: (displayedAiContent!.tools as Array<{ name: string; description?: string }>).map((tool, idx) => (
																<li key={idx} className="mb-2 d-flex align-items-start gap-2">
																	<i className="bi bi-wrench text-muted mt-1" aria-hidden />
																	<div>
																		<strong>{tool.name}</strong>
																		{tool.description && <span className="text-muted d-block small">{tool.description}</span>}
																	</div>
																</li>
															))}
													</ul>
												)
											) : (
												<p className="text-muted small mb-0">{isEditingPlanContent ? copy('No tools listed. Click + Add to add tools.', 'Keine Werkzeuge gelistet. Klicken Sie auf + Hinzufügen, um Werkzeuge hinzuzufügen.', 'Nema navedenih alata. Kliknite + Dodaj za dodavanje alata.') : copy('No tools listed.', 'Keine Werkzeuge gelistet.', 'Nema navedenih alata.')}</p>
											)}
										</div>
									</div>

									{isCompanyDirectFlow && plan?.databaseId && companyActorId != null && (
										<div className="card mt-4">
											<div className="card-header">{copy('Linked seeker visibility', 'Sichtbarkeit für verknüpften Suchenden', 'Vidljivost za povezanog vlasnika')}</div>
											<div className="card-body">
												<p className="text-muted small mb-2">
													{copy(
														'Use the same client-link settings here while you edit the quote-specific version of the plan.',
														'Verwalten Sie hier dieselben Einstellungen für den Kundenlink, während Sie die angebotsspezifische Version des Plans bearbeiten.',
														'Ovdje upravljajte istim postavkama poveznice klijenta dok uređujete verziju plana za ponudu.'
													)}
												</p>
												<div className="d-flex flex-wrap gap-2 small mb-3">
													<span className="badge text-bg-light border">
														{copy('Pricing', 'Preise', 'Cijene')}: {companyShareVisibility.pricing === 'full'
															? copy('full', 'voll', 'puno')
															: companyShareVisibility.pricing === 'total_only'
																? copy('total only', 'samo ukupno', 'samo ukupno')
																: copy('hidden', 'versteckt', 'skriveno')}
													</span>
													<span className={`badge border ${companyShareVisibility.timeline ? 'text-bg-success' : 'text-bg-light'}`}>{copy('Timeline', 'Zeitplan', 'Vremenski plan')}</span>
													<span className={`badge border ${companyShareVisibility.instructions ? 'text-bg-success' : 'text-bg-light'}`}>{copy('Instructions', 'Upute', 'Upute')}</span>
													<span className={`badge border ${companyShareVisibility.team ? 'text-bg-success' : 'text-bg-light'}`}>{copy('Team', 'Team', 'Tim')}</span>
												</div>
												<button
													type="button"
													className="btn btn-outline-success w-100"
													onClick={() => setShareClientModalOpen(true)}
												>
													{companyInviteStatus?.has_invite
														? copy('Update seeker view', 'Ansicht für Suchenden aktualisieren', 'Ažuriraj prikaz vlasnika')
														: copy('Set seeker view', 'Ansicht für Suchenden festlegen', 'Postavi prikaz vlasnika')}
												</button>
											</div>
										</div>
									)}

									{/* Actions */}
									<div className="card mt-4">
										<div className="card-header">{copy('Actions', 'Aktionen', 'Akcije')}</div>
										<div className="card-body">
											<button 
												className="btn btn-outline-primary w-100" 
												onClick={handleDownloadPDF}
											>
												{copy('Download PDF', 'PDF herunterladen', 'Preuzmi PDF')}
											</button>
										</div>
									</div>
								</div>
							</div>
						</>
					) : (
						// Fallback to old format for plans without AI content
						<div className="card mt-4">
							<div className="card-header d-flex justify-content-between align-items-center">
								<span>Plan Details</span>
								<button
									type="button"
									className="btn btn-outline-primary btn-sm"
									onClick={handleDownloadPDF}
								>
									Download PDF
								</button>
							</div>
							<div className="card-body">
								<div className="row g-3">
									<div className="col-12 col-lg-4">
										{(plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl) ? (
											<img
												src={getImageUrl(plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl)}
												alt={plan.title}
												className="img-fluid rounded border"
												style={{ width: '100%', objectFit: 'cover' }}
												onError={(e) => {
													console.error('Failed to load plan image');
													e.currentTarget.style.display = 'none';
												}}
											/>
										) : (
											<div className="bg-light border rounded d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
												<span className="text-muted small">{copy('No photo provided', 'Kein Foto vorhanden', 'Nema fotografije')}</span>
											</div>
										)}
									</div>
									<div className="col-12 col-lg-4">
										<div className="mb-3">
											<div className="text-muted small">{copy('Style', 'Stil', 'Stil')}</div>
											<div className="fw-semibold">{getLocalizedStyleLabel(plan.styleKey, stylePresetsByKey[plan.styleKey]?.title, copy)}</div>
										</div>
										{plan.questionnaire && (
											<>
												<div className="mb-3">
													<div className="text-muted small">{copy('Garden Size', 'Gartengröße', 'Veličina vrta')}</div>
													<div>{plan.questionnaire.gardenSize} m²</div>
												</div>
												{plan.questionnaire.location && (
													<div className="mb-3">
														<div className="text-muted small">{copy('Location', 'Standort', 'Lokacija')}</div>
														<div>{plan.questionnaire.location}</div>
													</div>
												)}
												{plan.questionnaire.projectFocus && (
													<div className="mb-3">
														<div className="text-muted small">{copy('Project focus', 'Projektfokus', 'Fokus projekta')}</div>
														<div>{localizeProjectFocus(plan.questionnaire.projectFocus)}</div>
													</div>
												)}
												<div className="mb-0">
													<div className="text-muted small">{copy('Seeker Timeline', 'Zeitplan des Suchenden', 'Vremenski plan vlasnika')}</div>
													<div>{seekerTimeline}</div>
												</div>
											</>
										)}
									</div>
									<div className="col-12 col-lg-4">
										{plan.notes && (
											<div className="mb-3">
												<div className="text-muted small">{copy('Notes', 'Hinweise', 'Bilješke')}</div>
												<p className="small mb-0">{plan.notes}</p>
											</div>
										)}
											<p className="text-muted small mb-0">
												{copy("This plan doesn't have design details yet - contact the seeker if you need more information.", 'Dieser Plan enthält noch keine Gestaltungsdetails - kontaktieren Sie den Suchenden, wenn Sie weitere Informationen benötigen.', 'Ovaj plan još nema detalje dizajna - kontaktirajte vlasnika ako trebate više informacija.')}
											</p>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Footer – always visible at bottom of workspace */}
			<div className="questionnaire-footer">
				<div className="d-flex justify-content-between gap-2 align-items-center">
					<div className="text-muted small d-none d-sm-block">
						{copy('Changes here apply only to this offer; the seeker\'s plan stays unchanged.', 'Änderungen hier gelten nur für dieses Angebot; der Plan des Suchenden bleibt unverändert.', 'Promjene ovdje vrijede samo za ovu ponudu; plan vlasnika ostaje nepromijenjen.')}
					</div>
					<div className="d-flex flex-wrap gap-2 justify-content-end">
						{user?.role !== 'seeker' && (
							<button
								className="btn btn-outline-danger btn-sm"
								type="button"
								onClick={handleReportSeekerFromOfferFlow}
								disabled={isReportingUser || !plan}
							>
								<i className="bi bi-flag me-1"></i>
								{isReportingUser ? copy('Sending...', 'Senden...', 'Slanje...') : copy('Report user', 'Nutzer melden', 'Prijavi korisnika')}
							</button>
						)}
						<button
							className="btn btn-success btn-sm"
							type="button"
							onClick={handleSendOffer}
							disabled={isSending}
						>
							{isSending
							? copy('Saving...', 'Speichern...', 'Spremanje...')
							: isCompanyDirectFlow && !clientIsLinked
								? copy('Save quote', 'Angebot speichern', 'Spremi ponudu')
								: copy('Send Offer', 'Angebot senden', 'Pošalji ponudu')}
						</button>
						{validationError && (
							<span className="text-danger small align-self-center ms-2">{validationError}</span>
						)}
					</div>
				</div>
			</div>

			{autoAssignChoiceOpen && (
				<>
					<div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true" style={{ zIndex: 1070 }}>
						<div className="modal-dialog modal-dialog-centered">
							<div className="modal-content">
								<div className="modal-header">
									<h5 className="modal-title">
										{copy('Auto-assign gardeners', 'Gärtner automatisch zuweisen', 'Automatska dodjela vrtlara')}
									</h5>
									<button
										type="button"
										className="btn-close"
										aria-label={copy('Close', 'Schließen', 'Zatvori')}
										onClick={() => setAutoAssignChoiceOpen(false)}
									/>
								</div>
								<div className="modal-body">
									<p className="mb-0 small">
										{copy(
											'Some tasks already have gardeners assigned. What should be updated?',
											'Einige Aufgaben haben bereits zugewiesene Gärtner. Was soll aktualisiert werden?',
											'Neki zadaci već imaju dodijeljene vrtlare. Što treba ažurirati?'
										)}
									</p>
									<p className="text-muted small mb-0 mt-2">
										{copy(
											'Assignments use your selected team, required skills, availability, and sequential scheduling.',
											'Zuweisungen nutzen Ihr ausgewähltes Team, erforderliche Fähigkeiten, Verfügbarkeit und die sequenzielle Planung.',
											'Dodjele koriste odabrani tim, potrebne vještine, dostupnost i sekvencijalno planiranje.'
										)}
									</p>
								</div>
								<div className="modal-footer flex-column flex-sm-row gap-2">
									<button
										type="button"
										className="btn btn-outline-secondary"
										onClick={() => setAutoAssignChoiceOpen(false)}
									>
										{copy('Cancel', 'Abbrechen', 'Odustani')}
									</button>
									<button
										type="button"
										className="btn btn-outline-success"
										onClick={() => {
											setAutoAssignChoiceOpen(false);
											runAutoAssignTimelineGardeners(true);
										}}
									>
										{copy('Only unassigned tasks', 'Nur nicht zugewiesene Aufgaben', 'Samo nedodijeljeni zadaci')}
									</button>
									<button
										type="button"
										className="btn btn-success"
										onClick={() => {
											setAutoAssignChoiceOpen(false);
											runAutoAssignTimelineGardeners(false);
										}}
									>
										{copy('All tasks', 'Alle Aufgaben', 'Svi zadaci')}
									</button>
								</div>
							</div>
						</div>
					</div>
					<div className="modal-backdrop fade show" style={{ zIndex: 1065 }} />
				</>
			)}

			{/* Image Modal */}
			{plan && displayedAiContent && (() => {
				const afterImageUrl = displayedAiContent.photoAfterUrl;
				const isPlaceholder = afterImageUrl?.includes('unsplash.com');
				const hasAfterImage = afterImageUrl && !isPlaceholder;
				const beforeImageUrl = plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl;
				
				const images: Array<{ url: string; alt: string; label: string }> = [];
				if (beforeImageUrl) {
					const url = getImageUrl(beforeImageUrl);
					if (url) {
						images.push({
							url,
							alt: 'Current State',
							label: 'Current State'
						});
					}
				}
				if (hasAfterImage && afterImageUrl) {
					const url = getImageUrl(afterImageUrl);
					if (url) {
						images.push({
							url,
							alt: 'Design vision',
							label: 'Design vision'
						});
					}
				}
				
				return (
					<ImageModal
						isOpen={imageModalOpen}
						onClose={() => setImageModalOpen(false)}
						images={images}
						initialIndex={imageModalIndex}
					/>
				);
			})()}

			{/* Regenerate with changes modal */}
			{editModalOpen && (
				<div
					className="modal d-block"
					style={{
						backgroundColor: 'rgba(0,0,0,0.5)',
						position: 'fixed',
						inset: 0,
						zIndex: 20000,
						overflowY: 'auto'
					}}
					aria-modal="true"
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{copy('Change something?', 'Etwas ändern?', 'Promijeniti nešto?')}</h5>
								<button type="button" className="btn-close" onClick={() => !isRegenerating && setEditModalOpen(false)} aria-label="Close" />
							</div>
							{isRegenerating ? (
								<div className="modal-body py-4">
									<PlanGenerationPuzzleAnimation
										beforeImageUrl={plan.questionnaire?.photoBeforeUrl || plan.photoBeforeUrl}
										selectedComponents={plan.questionnaire?.selectedComponents}
										subMessage={copy("We're applying your requested changes and regenerating your plan. This usually takes about 1-3 minutes.", 'Wir setzen Ihre gewünschten Änderungen um und erstellen Ihren Plan neu. Dies dauert normalerweise 1-3 Minuten.', 'Primjenjujemo vaše tražene izmjene i ponovno generiramo vaš plan. To obično traje 1-3 minute.')}
									/>
								</div>
							) : (
								<>
									<div className="modal-body">
										<p className="text-muted small mb-2">
											{copy(
												"Describe what you'd like to change. We'll regenerate the image and plan to match—from small tweaks (e.g. fence colour) to bigger changes (e.g. different style).",
												"Beschreiben Sie, was Sie ändern möchten. Wir generieren Bild und Plan entsprechend neu – von kleinen Anpassungen (z. B. Zaunfarbe) bis zu größeren Änderungen (z. B. anderer Stil).",
												"Opišite što želite promijeniti. Ponovno ćemo generirati sliku i plan prema tome — od manjih prilagodbi (npr. boja ograde) do većih promjena (npr. drugačiji stil)."
											)}
										</p>
										<textarea
											className="form-control"
											rows={3}
											placeholder={copy('e.g. different colour fence, more flowers, make it more modern, less lawn...', 'z. B. andere Zaunfarbe, mehr Blumen, modernerer Stil, weniger Rasen...', 'npr. druga boja ograde, više cvijeća, moderniji stil, manje travnjaka...')}
											value={editInstructions}
											onChange={(e) => setEditInstructions(e.target.value)}
											disabled={isRegenerating}
										/>
									</div>
									<div className="modal-footer">
										<button type="button" className="btn btn-secondary" onClick={() => setEditModalOpen(false)} disabled={isRegenerating}>
											{copy('Cancel', 'Abbrechen', 'Odustani')}
										</button>
										<button type="button" className="btn btn-success" onClick={handleRegenerateWithChanges} disabled={isRegenerating || !editInstructions.trim()}>
											<>{copy('Regenerate', 'Neu generieren', 'Ponovno generiraj')} <i className="bi bi-arrow-repeat ms-1"></i></>
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}

			{isCompanyDirectFlow && plan?.databaseId && companyActorId != null && (
				<ShareClientLinkModal
					open={shareClientModalOpen}
					onClose={() => {
						setShareClientModalOpen(false);
						void getCompanyInviteForPlan(plan.databaseId!, companyActorId)
							.then((res) => setCompanyInviteStatus(res))
							.catch(() => {});
					}}
					planDatabaseId={plan.databaseId}
					companyUserId={companyActorId}
					copy={copy}
					offerId={shareOfferId ?? undefined}
					forQuoteShare
					existingInviteCode={companyInviteStatus?.code ?? null}
					existingInviteEmail={companyInviteStatus?.invited_email ?? null}
				/>
			)}
		</div>
	);
}


